import React, { useState, useEffect } from 'react';
import { FoxSpinnerInline } from '../ui/FoxSpinner';
import api from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Users, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import EventExclusionsManager from './EventExclusionsManager';
import HostNoticesManagement from './HostNoticesManagement';
import KakoTokenManager from './KakoTokenManager';
import KakoDmTokenManager from './KakoDmTokenManager';
import FollowCleanupPanel from './FollowCleanupPanel';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Componente para item arrastável
const SortableHostItem = ({ host, position, liveLinks, livePriorities, editingPriority, setEditingPriority, handleSavePriority, Input }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: host.showId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 bg-gradient-to-r from-red-50 to-white border-l-4 border-red-500 rounded-lg hover:shadow-md transition-shadow ${isDragging ? 'shadow-lg ring-2 ring-purple-400' : ''}`}
    >
      {/* Handle para arrastar */}
      <div
        {...attributes}
        {...listeners}
        className="flex-shrink-0 cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded"
        title="Arraste para reordenar"
      >
        <GripVertical className="w-5 h-5 text-gray-400" />
      </div>
      
      {/* Posição */}
      <div className="flex-shrink-0 w-10">
        <span className="inline-flex items-center justify-center w-8 h-8 bg-purple-100 text-purple-800 font-bold text-sm rounded-full border border-purple-300">
          {position}
        </span>
      </div>
      
      {/* Avatar */}
      <div className="relative flex-shrink-0">
        <img
          src={host.avatar}
          alt={host.nickname}
          className="w-12 h-12 sm:w-14 sm:h-14 rounded-full border-2 border-red-500"
        />
        <div className="absolute -top-1 -right-1">
          {host.lStatus === 32 ? (
            <span className="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 bg-blue-500 rounded-full border-2 border-white text-white text-xs font-bold">
              ∞
            </span>
          ) : (
            <span className="w-3 h-3 sm:w-4 sm:h-4 bg-red-600 rounded-full animate-pulse border-2 border-white"></span>
          )}
        </div>
      </div>
      
      {/* Nickname */}
      <div className="flex-1 min-w-0">
        <p className="font-bold text-sm sm:text-base truncate text-gray-900">
          {host.nickname}
        </p>
        <p className="text-xs text-gray-500">ID: {host.showId}</p>
      </div>
      
      {/* Tag SEM LINK ou Link */}
      <div className="flex-shrink-0">
        {liveLinks[host.showId] ? (
          <span className="text-xs sm:text-sm font-semibold text-green-600">
            ✓ Link cadastrado
          </span>
        ) : (
          <span className="text-xs sm:text-sm font-bold bg-yellow-100 text-yellow-800 px-2 py-1 rounded border border-yellow-400">
            SEM LINK
          </span>
        )}
      </div>
      
      {/* Indicador Live ou Conexão */}
      <div className="flex-shrink-0">
        {host.lStatus === 32 ? (
          <span className="text-xs sm:text-sm font-bold text-blue-600">
            ∞ CONEXÃO
          </span>
        ) : (
          <span className="text-xs sm:text-sm font-bold text-red-600 animate-pulse">
            🔴 LIVE
          </span>
        )}
      </div>
    </div>
  );
};

const AgencyManagement = () => {
  const [agency, setAgency] = useState(null);
  const [allMembers, setAllMembers] = useState([]);
  const [liveHosts, setLiveHosts] = useState([]);
  const [hostsData, setHostsData] = useState([]);
  const [dataSummary, setDataSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [loadingLive, setLoadingLive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dataPeriod, setDataPeriod] = useState('week');
  const [dataDateRange, setDataDateRange] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  // Estados para links de live
  const [liveLinks, setLiveLinks] = useState({});
  const [livePriorities, setLivePriorities] = useState({});
  const [editingLink, setEditingLink] = useState({});
  const [editingPriority, setEditingPriority] = useState({});
  const [showLiveBar, setShowLiveBar] = useState(false);
  
  // Estados para período customizado
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [loadingCustomData, setLoadingCustomData] = useState(false);
  
  // Estados para evento Trabalhou Ganhou
  const [workEventHosts, setWorkEventHosts] = useState([]);
  const [workEventConfig, setWorkEventConfig] = useState(null);
  const [loadingWorkEvent, setLoadingWorkEvent] = useState(false);
  const [workEventHistory, setWorkEventHistory] = useState([]);
  const [workEventSortBy, setWorkEventSortBy] = useState('week_desc');
  const [workEventDateFilter, setWorkEventDateFilter] = useState({ start_date: '', end_date: '' });
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [syncingWorkEvent, setSyncingWorkEvent] = useState(false);
  const [workEventFilter, setWorkEventFilter] = useState('');
  
  // Estados para configurações do evento Trabalhou Ganhou
  const [workEventSettings, setWorkEventSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [editedPointsTable, setEditedPointsTable] = useState([]);
  const [editedPresenceMultiplier, setEditedPresenceMultiplier] = useState({});
  const [editedCrystalsMultiplier, setEditedCrystalsMultiplier] = useState(2000);
  
  // Estados para Ranking de Estrelas
  const [starRankingConfig, setStarRankingConfig] = useState(null);
  const [starRanking, setStarRanking] = useState([]);
  const [loadingStarRanking, setLoadingStarRanking] = useState(false);
  const [editingPercentages, setEditingPercentages] = useState(false);
  const [tempPercentages, setTempPercentages] = useState({
    top1: 2.5,
    top2: 2.0,
    top3: 1.5
  });
  const [starRankingHistory, setStarRankingHistory] = useState([]);
  const [starRankingSortBy, setStarRankingSortBy] = useState('week_desc');
  const [starRankingDateFilter, setStarRankingDateFilter] = useState({ start_date: '', end_date: '' });
  const [loadingStarHistory, setLoadingStarHistory] = useState(false);
  const [starHistoryFilter, setStarHistoryFilter] = useState('');
  // Seleção em massa - resgates
  const [selectedWorkClaims, setSelectedWorkClaims] = useState([]);
  const [selectedStarClaims, setSelectedStarClaims] = useState([]);
  const [deletingClaims, setDeletingClaims] = useState(false);
  
  // Estados para recompensas perdidas
  const [workEventExpired, setWorkEventExpired] = useState([]);
  const [loadingWorkExpired, setLoadingWorkExpired] = useState(false);
  const [workExpiredFilter, setWorkExpiredFilter] = useState('');
  const [starExpired, setStarExpired] = useState([]);
  const [loadingStarExpired, setLoadingStarExpired] = useState(false);
  const [starExpiredFilter, setStarExpiredFilter] = useState('');

  // Sensores para drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Precisa mover 8px para ativar o drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  useEffect(() => {
    loadAgencyInfo();
    loadAllMembers();
    loadLiveLinks();
    loadLiveBarSetting();
    loadWorkEventConfig();
    loadStarRankingConfig();
  }, []);

  // Auto-refresh hosts ao vivo a cada 30s
  useEffect(() => {
    let interval;
    if (autoRefresh && liveHosts.length > 0) {
      interval = setInterval(() => {
        if (document.visibilityState === 'visible') {
          loadLiveHosts();
        }
      }, 30000);
    }
    return () => clearInterval(interval);
  }, [autoRefresh, liveHosts.length]);

  const loadAgencyInfo = async () => {
    try {
      const response = await api.get('/agency/my');
      setAgency(response.data.agency);
    } catch (error) {
      console.error('Erro ao carregar agência:', error);
      toast.error('Erro ao carregar dados da agência');
    }
  };

  const loadAllMembers = async () => {
    setLoading(true);
    
    try {
      let allMembersList = [];
      let page = 1;
      let hasMore = true;
      const PAGE_SIZE = 100;
      
      while (hasMore) {
        const response = await api.get(`/agency/members?page=${page}&page_size=${PAGE_SIZE}&show_id=`);
        
        const membersList = response.data.list || [];
        allMembersList = [...allMembersList, ...membersList];
        
        const total = response.data.total || 0;
        hasMore = allMembersList.length < total;
        page++;
      }
      
      setAllMembers(allMembersList);
      toast.success(`${allMembersList.length} membros carregados!`);
      
    } catch (error) {
      console.error('Erro ao carregar membros:', error);
      toast.error('Erro ao carregar lista de membros');
    } finally {
      setLoading(false);
    }
  };

  const loadLiveHosts = async () => {
    setLoadingLive(true);
    
    try {
      // Buscar TODAS as páginas para encontrar hosts ao vivo
      let allMembersList = [];
      let page = 1;
      let hasMore = true;
      const PAGE_SIZE = 100;
      
      while (hasMore) {
        const response = await api.get(`/agency/members?page=${page}&page_size=${PAGE_SIZE}&show_id=`);
        
        const membersList = response.data.list || [];
        allMembersList = [...allMembersList, ...membersList];
        
        const total = response.data.total || 0;
        hasMore = allMembersList.length < total;
        page++;
      }
      
      // Filtrar apenas quem está AO VIVO
      const live = allMembersList.filter(m => m.isLiving === true || m.lStatus > 0);
      setLiveHosts(live);
      
      console.log(`🔴 Total buscado: ${allMembersList.length} | Ao vivo: ${live.length}`);
    } catch (error) {
      console.error('Erro ao carregar hosts ao vivo:', error);
    } finally {
      setLoadingLive(false);
    }
  };

  const [starHosts, setStarHosts] = useState(null);
  const [loadingStarHosts, setLoadingStarHosts] = useState(false);
  const [starFilter, setStarFilter] = useState('all');

  const loadStarHosts = async (force = false) => {
    if (starHosts && !force) return;
    setLoadingStarHosts(true);
    try {
      const res = await api.get('/agency/star-hosts');
      setStarHosts(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao carregar Star Hosts');
    }
    setLoadingStarHosts(false);
  };

  const loadHostsData = async (period = 'week') => {
    setLoadingData(true);
    
    try {
      const response = await api.get(`/agency/data/hosts?period=${period}`);
      
      setHostsData(response.data.hosts || []);
      setDataSummary(response.data.summary || null);
      setDataDateRange(`${response.data.start_date} a ${response.data.end_date}`);
      setDataPeriod(period);
      toast.success(`Dados de ${response.data.total_hosts} hosts carregados!`);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados dos hosts');
    } finally {
      setLoadingData(false);
    }
  };

  const loadLiveLinks = async () => {
    try {
      const response = await api.get('/agency/live-links');
      const linksObj = {};
      const prioritiesObj = {};
      response.data.links.forEach(link => {
        linksObj[link.show_id] = link.live_link;
        prioritiesObj[link.show_id] = link.priority || 9999;
      });
      setLiveLinks(linksObj);
      setLivePriorities(prioritiesObj);
    } catch (error) {
      console.error('Erro ao carregar links de live:', error);
    }
  };

  const loadLiveBarSetting = async () => {
    try {
      const response = await api.get('/agency/settings/live-bar');
      setShowLiveBar(response.data.show_live_bar);
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
    }
  };

  const handleSaveLiveLink = async (showId, link) => {
    try {
      if (!link || link.trim() === '') {
        toast.error('Link não pode estar vazio');
        return;
      }

      await api.post('/agency/live-link', null, {
        params: { show_id: showId, live_link: link.trim() }
      });
      
      setLiveLinks({ ...liveLinks, [showId]: link.trim() });
      setEditingLink({ ...editingLink, [showId]: '' });
      toast.success('Link salvo com sucesso!');
      await loadLiveLinks();
    } catch (error) {
      console.error('Erro ao salvar link:', error);
      toast.error('Erro ao salvar link');
    }
  };

  const handleDeleteLiveLink = async (showId) => {
    try {
      await api.delete(`/agency/live-link/${showId}`);
      
      const newLinks = { ...liveLinks };
      delete newLinks[showId];
      setLiveLinks(newLinks);
      
      const newPriorities = { ...livePriorities };
      delete newPriorities[showId];
      setLivePriorities(newPriorities);
      
      toast.success('Link removido com sucesso!');
    } catch (error) {
      console.error('Erro ao remover link:', error);
      toast.error('Erro ao remover link');
    }
  };

  const handleSavePriority = async (showId, priority) => {
    try {
      const priorityNum = parseInt(priority) || 9999;
      
      await api.put(`/agency/live-link/${showId}/priority`, null, {
        params: { priority: priorityNum }
      });
      
      setLivePriorities({ ...livePriorities, [showId]: priorityNum });
      setEditingPriority({ ...editingPriority, [showId]: false });
      toast.success(`Prioridade ${priorityNum} salva!`);
      await loadLiveLinks();
    } catch (error) {
      console.error('Erro ao salvar prioridade:', error);
      toast.error('Erro ao salvar prioridade');
    }
  };

  // Handler para quando o drag termina - reordena e salva prioridades
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      return;
    }
    
    // Ordenar hosts atuais por prioridade
    const sortedHosts = [...liveHosts].sort(
      (a, b) => (livePriorities[a.showId] || 9999) - (livePriorities[b.showId] || 9999)
    );
    
    const oldIndex = sortedHosts.findIndex(h => h.showId === active.id);
    const newIndex = sortedHosts.findIndex(h => h.showId === over.id);
    
    if (oldIndex === -1 || newIndex === -1) return;
    
    // Reordenar
    const reorderedHosts = arrayMove(sortedHosts, oldIndex, newIndex);
    
    // Atualizar prioridades localmente primeiro (UX mais rápida)
    const newPriorities = { ...livePriorities };
    reorderedHosts.forEach((host, index) => {
      newPriorities[host.showId] = index + 1;
    });
    setLivePriorities(newPriorities);
    
    // Salvar todas as prioridades no backend
    try {
      const updatePromises = reorderedHosts.map((host, index) =>
        api.put(`/agency/live-link/${host.showId}/priority`, null, {
          params: { priority: index + 1 }
        })
      );
      
      await Promise.all(updatePromises);
      toast.success('Ordem atualizada!');
    } catch (error) {
      console.error('Erro ao salvar ordem:', error);
      toast.error('Erro ao salvar ordem');
      // Recarregar em caso de erro
      await loadLiveLinks();
    }
  };

  const handleToggleLiveBar = async (enabled) => {
    try {
      await api.put('/agency/settings/live-bar', null, {
        params: { show_live_bar: enabled }
      });
      
      setShowLiveBar(enabled);
      toast.success(enabled ? 'Barra ativada!' : 'Barra desativada!');
    } catch (error) {
      console.error('Erro ao atualizar configuração:', error);
      toast.error('Erro ao atualizar configuração');
    }
  };

  const loadCustomPeriodData = async () => {
    if (!customStartDate || !customEndDate) {
      toast.error('Selecione data inicial e final');
      return;
    }

    const start = new Date(customStartDate);
    const end = new Date(customEndDate);
    const today = new Date();
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(today.getMonth() - 3);

    // Validações
    if (start > today) {
      toast.error('Data inicial não pode ser futura');
      return;
    }
    if (end > today) {
      toast.error('Data final não pode ser futura');
      return;
    }
    if (start < threeMonthsAgo) {
      toast.error('Data inicial não pode ser mais antiga que 3 meses');
      return;
    }
    if (start > end) {
      toast.error('Data inicial deve ser anterior à data final');
      return;
    }

    setLoadingCustomData(true);
    
    try {
      const response = await api.get(`/agency/data/hosts/custom`, {
        params: {
          start_date: customStartDate,
          end_date: customEndDate
        }
      });
      
      setHostsData(response.data.hosts || []);
      setDataSummary(response.data.summary || null);
      setDataDateRange(`${response.data.start_date} a ${response.data.end_date}`);
      setDataPeriod('custom');
      toast.success(`Dados de ${response.data.total_hosts} hosts carregados!`);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados do período customizado');
    } finally {
      setLoadingCustomData(false);
    }
  };

  const handleExportData = async () => {
    try {
      let url = `/agency/data/hosts/export?period=${dataPeriod}`;
      
      if (dataPeriod === 'custom' && customStartDate && customEndDate) {
        url += `&start_date=${customStartDate}&end_date=${customEndDate}`;
      }
      
      const response = await api.get(url, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `dados_hosts_${dataPeriod}_${new Date().getTime()}.xlsx`;
      link.click();
      
      toast.success('Dados exportados com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar:', error);
      toast.error('Erro ao exportar dados');
    }
  };

  // Calcular data mínima e máxima para o calendário
  const getMinDate = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 3);
    return date.toISOString().split('T')[0];
  };

  const getMaxDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  const loadWorkEventConfig = async () => {
    try {
      const response = await api.get('/host-work-event/config');
      setWorkEventConfig(response.data);
    } catch (error) {
      console.error('Erro ao carregar config do evento:', error);
    }
  };

  const loadWorkEventSettings = async () => {
    setLoadingSettings(true);
    try {
      const response = await api.get('/host-work-event/settings');
      const data = response.data;
      setWorkEventSettings(data);
      setEditedPointsTable(data.points_table || []);
      setEditedPresenceMultiplier(data.presence_multiplier || {});
      setEditedCrystalsMultiplier(data.crystals_multiplier || 2000);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleSaveWorkEventSettings = async () => {
    // Validar dados
    if (editedPointsTable.length === 0) {
      toast.error('Adicione pelo menos uma meta');
      return;
    }

    // Verificar se todas as metas têm valores válidos
    for (const row of editedPointsTable) {
      if (row.goal < 0 || row.min_hours <= 0 || row.points < 0) {
        toast.error('Valores inválidos na tabela de metas');
        return;
      }
    }

    setSavingSettings(true);
    try {
      const response = await api.put('/host-work-event/settings', {
        points_table: editedPointsTable,
        presence_multiplier: editedPresenceMultiplier,
        crystals_multiplier: editedCrystalsMultiplier
      });
      setWorkEventSettings(response.data);
      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      toast.error('Erro ao salvar configurações');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleResetWorkEventSettings = async () => {
    if (!window.confirm('Restaurar configurações padrão? Isso irá reverter todas as alterações para os valores originais.')) {
      return;
    }

    setSavingSettings(true);
    try {
      const response = await api.post('/host-work-event/settings/reset');
      setWorkEventSettings(response.data);
      setEditedPointsTable(response.data.points_table || []);
      setEditedPresenceMultiplier(response.data.presence_multiplier || {});
      setEditedCrystalsMultiplier(response.data.crystals_multiplier || 2000);
      toast.success('Configurações restauradas para o padrão!');
    } catch (error) {
      console.error('Erro ao restaurar configurações:', error);
      toast.error('Erro ao restaurar configurações');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleAddGoal = () => {
    const newGoal = {
      goal: 0,
      min_hours: 2,
      points: 0.5
    };
    setEditedPointsTable([...editedPointsTable, newGoal]);
  };

  const handleRemoveGoal = (index) => {
    if (editedPointsTable.length <= 1) {
      toast.error('Deve haver pelo menos uma meta');
      return;
    }
    const newTable = editedPointsTable.filter((_, i) => i !== index);
    setEditedPointsTable(newTable);
  };

  const handleUpdateGoal = (index, field, value) => {
    const newTable = [...editedPointsTable];
    newTable[index] = { ...newTable[index], [field]: value };
    setEditedPointsTable(newTable);
  };

  const handleUpdatePresenceMultiplier = (days, value) => {
    setEditedPresenceMultiplier({
      ...editedPresenceMultiplier,
      [days]: parseFloat(value) || 0
    });
  };

  const handleAddPresenceMultiplier = () => {
    // Encontrar próximo dia disponível (1-7)
    const existingDays = Object.keys(editedPresenceMultiplier).map(d => parseInt(d));
    let newDay = 1;
    for (let i = 1; i <= 7; i++) {
      if (!existingDays.includes(i)) {
        newDay = i;
        break;
      }
    }
    if (existingDays.length >= 7) {
      toast.error('Máximo de 7 dias já configurados');
      return;
    }
    setEditedPresenceMultiplier({
      ...editedPresenceMultiplier,
      [newDay]: 1.0
    });
  };

  const handleRemovePresenceMultiplier = (days) => {
    const keys = Object.keys(editedPresenceMultiplier);
    if (keys.length <= 1) {
      toast.error('Deve haver pelo menos um multiplicador');
      return;
    }
    const newMultipliers = { ...editedPresenceMultiplier };
    delete newMultipliers[days];
    setEditedPresenceMultiplier(newMultipliers);
  };

  const handleUpdatePresenceDay = (oldDay, newDay) => {
    const newDayInt = parseInt(newDay);
    if (newDayInt < 1 || newDayInt > 7) {
      toast.error('Dias devem ser entre 1 e 7');
      return;
    }
    // Verificar se o novo dia já existe
    if (oldDay !== newDay && editedPresenceMultiplier[newDayInt] !== undefined) {
      toast.error(`Dia ${newDayInt} já existe`);
      return;
    }
    const newMultipliers = { ...editedPresenceMultiplier };
    const value = newMultipliers[oldDay];
    delete newMultipliers[oldDay];
    newMultipliers[newDayInt] = value;
    setEditedPresenceMultiplier(newMultipliers);
  };

  const loadWorkEventHosts = async () => {
    setLoadingWorkEvent(true);
    try {
      const response = await api.get('/host-work-event/all-hosts-data');
      setWorkEventHosts(response.data.hosts || []);
    } catch (error) {
      console.error('Erro ao carregar dados do evento:', error);
      toast.error('Erro ao carregar dados do evento');
    } finally {
      setLoadingWorkEvent(false);
    }
  };

  const handleToggleWorkEvent = async (active) => {
    try {
      await api.put('/host-work-event/config', null, {
        params: { active }
      });
      setWorkEventConfig({ ...workEventConfig, active });
      toast.success(active ? 'Evento ativado!' : 'Evento desativado!');
    } catch (error) {
      console.error('Erro ao atualizar evento:', error);
      toast.error('Erro ao atualizar evento');
    }
  };

  const handleSyncWorkEventData = async () => {
    setSyncingWorkEvent(true);
    try {
      const response = await api.post('/host-work-event/sync-daily-data');
      toast.success(response.data.message);
      await loadWorkEventHosts();
    } catch (error) {
      console.error('Erro ao sincronizar:', error);
      toast.error('Erro ao sincronizar dados');
    } finally {
      setSyncingWorkEvent(false);
    }
  };

  const handleRefinalizePreviousWeek = async () => {
    if (!window.confirm('Refinilizar semana passada? Isso irá DELETAR e RECALCULAR todas as recompensas da semana anterior. Continuar?')) {
      return;
    }

    try {
      // Calcular semana passada
      const now = new Date();
      const daysSinceMonday = now.getDay() === 0 ? 6 : now.getDay() - 1;
      const thisMonday = new Date(now);
      thisMonday.setDate(now.getDate() - daysSinceMonday);
      
      const lastMonday = new Date(thisMonday);
      lastMonday.setDate(thisMonday.getDate() - 7);
      
      const weekStart = lastMonday.toISOString().split('T')[0];
      
      toast.info(`Refinalizando semana ${weekStart}...`);
      
      // 1. Limpar Trabalhou Ganhou
      await api.delete(`/host-work-event/finalize-week/${weekStart}`);
      
      // 2. Limpar Ranking Estrelas
      await api.delete(`/star-ranking/finalize-week/${weekStart}`);
      
      // 3. Refinilizar Trabalhou Ganhou
      const response1 = await api.post('/host-work-event/finalize-week', null, {
        params: { week_start: weekStart }
      });
      
      // 4. Refinilizar Ranking Estrelas
      const response2 = await api.post('/star-ranking/finalize-week', null, {
        params: { week_start: weekStart }
      });
      
      toast.success(`Semana ${weekStart} refinilizada com sucesso! Hosts podem resgatar após segunda 09:00.`);
    } catch (error) {
      console.error('Erro ao refinilizar:', error);
      toast.error(error.response?.data?.detail || 'Erro ao refinilizar semana');
    }
  };

  const loadWorkEventHistory = async (showIdFilter = '') => {
    setLoadingHistory(true);
    try {
      const params = showIdFilter ? { show_id: showIdFilter } : {};
      const response = await api.get('/host-work-event/history', { params });
      const data = response.data || [];
      
      // Agrupar por semana
      const grouped = data.reduce((acc, reward) => {
        const week = reward.week_start;
        if (!acc[week]) {
          acc[week] = {
            week_start: reward.week_start,
            week_end: reward.week_end,
            rewards: [],
            total_coins: 0
          };
        }
        acc[week].rewards.push(reward);
        acc[week].total_coins += reward.crystals_earned || 0;
        return acc;
      }, {});
      
      // Converter para array ordenado por semana (mais recente primeiro)
      const groupedArray = Object.values(grouped).sort((a, b) => 
        new Date(b.week_start) - new Date(a.week_start)
      );
      
      setWorkEventHistory(groupedArray);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      toast.error('Erro ao carregar histórico');
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadStarRankingHistory = async (showIdFilter = '') => {
    setLoadingStarHistory(true);
    try {
      const params = showIdFilter ? { show_id: showIdFilter } : {};
      const response = await api.get('/star-ranking/claims-history', { params });
      const data = response.data?.claims || [];
      
      // Agrupar por semana
      const grouped = data.reduce((acc, claim) => {
        const week = claim.week_start;
        if (!acc[week]) {
          acc[week] = {
            week_start: claim.week_start,
            week_end: claim.week_end,
            claims: [],
            total_coins: 0
          };
        }
        acc[week].claims.push(claim);
        acc[week].total_coins += claim.bonus_coins || 0;
        return acc;
      }, {});
      
      // Converter para array ordenado por semana (mais recente primeiro)
      const groupedArray = Object.values(grouped).sort((a, b) => 
        new Date(b.week_start) - new Date(a.week_start)
      );
      
      setStarRankingHistory(groupedArray);
    } catch (error) {
      console.error('Erro ao carregar histórico star ranking:', error);
      toast.error('Erro ao carregar histórico');
    } finally {
      setLoadingStarHistory(false);
    }
  };

  const toggleWorkClaim = (claimId) => {
    setSelectedWorkClaims((prev) =>
      prev.includes(claimId) ? prev.filter((id) => id !== claimId) : [...prev, claimId]
    );
  };

  const toggleAllWorkClaims = (weekRewards, checked) => {
    const ids = weekRewards.map((r) => r.id);
    setSelectedWorkClaims((prev) =>
      checked
        ? Array.from(new Set([...prev, ...ids]))
        : prev.filter((id) => !ids.includes(id))
    );
  };

  const deleteSelectedWorkClaims = async () => {
    if (selectedWorkClaims.length === 0) return;
    if (!window.confirm(`Desfazer ${selectedWorkClaims.length} resgate(s) do Trabalhou Ganhou?\n\nO host volta a poder resgatar a semana.\n⚠️ Moedas já creditadas no KAKO NÃO são revertidas.`)) {
      return;
    }
    setDeletingClaims(true);
    try {
      const res = await api.post('/host-work-event/claims/delete-bulk', { claim_ids: selectedWorkClaims });
      toast.success(`${res.data.updated} resgate(s) desfeito(s)`);
      setSelectedWorkClaims([]);
      await loadWorkEventHistory(workEventFilter);
    } catch (error) {
      toast.error('Erro ao desfazer resgates');
    } finally {
      setDeletingClaims(false);
    }
  };

  const toggleStarClaim = (claimId) => {
    setSelectedStarClaims((prev) =>
      prev.includes(claimId) ? prev.filter((id) => id !== claimId) : [...prev, claimId]
    );
  };

  const toggleAllStarClaims = (weekClaims, checked) => {
    const ids = weekClaims.map((c) => c.id);
    setSelectedStarClaims((prev) =>
      checked
        ? Array.from(new Set([...prev, ...ids]))
        : prev.filter((id) => !ids.includes(id))
    );
  };

  const deleteSelectedStarClaims = async () => {
    if (selectedStarClaims.length === 0) return;
    if (!window.confirm(`Desfazer ${selectedStarClaims.length} resgate(s) do Ranking de Estrelas?\n\nO host volta a poder resgatar a semana caso ainda esteja no TOP3.\n⚠️ Moedas já creditadas no KAKO NÃO são revertidas.`)) {
      return;
    }
    setDeletingClaims(true);
    try {
      const res = await api.post('/star-ranking/claims/delete-bulk', { claim_ids: selectedStarClaims });
      toast.success(`${res.data.updated} resgate(s) desfeito(s)`);
      setSelectedStarClaims([]);
      await loadStarRankingHistory(starHistoryFilter);
    } catch (error) {
      toast.error('Erro ao desfazer resgates');
    } finally {
      setDeletingClaims(false);
    }
  };

  const loadWorkEventExpired = async (showIdFilter = '') => {
    setLoadingWorkExpired(true);
    try {
      const params = showIdFilter ? { show_id: showIdFilter } : {};
      const response = await api.get('/host-work-event/expired-rewards', { params });
      setWorkEventExpired(response.data?.rewards || []);
    } catch (error) {
      console.error('Erro ao carregar recompensas perdidas:', error);
      toast.error('Erro ao carregar recompensas perdidas');
    } finally {
      setLoadingWorkExpired(false);
    }
  };

  const loadStarExpired = async (showIdFilter = '') => {
    setLoadingStarExpired(true);
    try {
      const params = showIdFilter ? { show_id: showIdFilter } : {};
      const response = await api.get('/star-ranking/expired-rewards', { params });
      setStarExpired(response.data?.rewards || []);
    } catch (error) {
      console.error('Erro ao carregar recompensas perdidas star:', error);
      toast.error('Erro ao carregar recompensas perdidas');
    } finally {
      setLoadingStarExpired(false);
    }
  };

  const loadStarRankingConfig = async () => {
    try {
      const response = await api.get('/star-ranking/config');
      setStarRankingConfig(response.data);
    } catch (error) {
      console.error('Erro ao carregar config star ranking:', error);
    }
  };

  const loadStarRanking = async () => {
    setLoadingStarRanking(true);
    try {
      const response = await api.get('/star-ranking/current-week');
      setStarRanking(response.data.ranking || []);
    } catch (error) {
      console.error('Erro ao carregar ranking:', error);
      toast.error('Erro ao carregar ranking');
    } finally {
      setLoadingStarRanking(false);
    }
  };

  const handleToggleStarRanking = async (active) => {
    try {
      await api.put('/star-ranking/config', null, {
        params: { active }
      });
      setStarRankingConfig({ ...starRankingConfig, active });
      toast.success(active ? 'Ranking ativado!' : 'Ranking desativado!');
    } catch (error) {
      console.error('Erro ao atualizar:', error);
      toast.error('Erro ao atualizar');
    }
  };

  const handleSavePercentages = async () => {
    try {
      await api.put('/star-ranking/config', null, {
        params: {
          top1_percentage: tempPercentages.top1,
          top2_percentage: tempPercentages.top2,
          top3_percentage: tempPercentages.top3
        }
      });
      
      setStarRankingConfig({
        ...starRankingConfig,
        top1_percentage: tempPercentages.top1,
        top2_percentage: tempPercentages.top2,
        top3_percentage: tempPercentages.top3
      });
      
      setEditingPercentages(false);
      toast.success('Percentuais atualizados!');
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar percentuais');
    }
  };

  const formatDiamonds = (value) => {
    // Retorna valor integral com separador de milhares
    return value.toLocaleString('pt-BR');
  };

  const formatMinutes = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}min` : `${mins}min`;
  };

  const filteredMembers = searchTerm
    ? allMembers.filter(m => 
        m.showId?.includes(searchTerm) ||
        m.nickname?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : allMembers;

  if (loading && !agency) {
    return (
      <div className="flex justify-center p-8">
        <FoxSpinnerInline size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Gestão do Token da Agência KAKO (hot-swap sem redeploy) */}
      <KakoTokenManager onTokenUpdated={() => { loadAgencyInfo(); loadAllMembers(); }} />
      <KakoDmTokenManager />
      <FollowCleanupPanel />

      {/* Header da Agência */}
      {agency && (
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              {agency.imageUrl && (
                <img 
                  src={agency.imageUrl} 
                  alt={agency.name} 
                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-4 border-purple-500"
                />
              )}
              <div className="text-center sm:text-left">
                <CardTitle className="text-xl sm:text-2xl">{agency.name}</CardTitle>
                <p className="text-sm text-gray-600">ID: #{agency.showId || agency.numId}</p>
                <div className="flex gap-2 mt-2 flex-wrap justify-center sm:justify-start">
                  <span className="text-sm font-semibold text-purple-600">
                    <Users className="w-3 h-3 inline mr-1" />
                    {agency.memberNum} membros
                  </span>
                  <span className="text-sm font-semibold text-blue-600">
                    Taxa: {(agency.ratio * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          </CardHeader>
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="members" className="w-full">
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="members" className="text-xs sm:text-sm">
            <Users className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Lista de Membros</span>
            <span className="sm:hidden">Membros</span>
          </TabsTrigger>
          <TabsTrigger value="live" onClick={() => loadLiveHosts()} className="text-xs sm:text-sm">
            🔴 <span className="hidden sm:inline">Ao Vivo</span><span className="sm:hidden">Live</span>
          </TabsTrigger>
          <TabsTrigger value="data" onClick={() => !hostsData.length && loadHostsData('week')} className="text-xs sm:text-sm">
            📊 Dados
          </TabsTrigger>
          <TabsTrigger value="star" onClick={() => loadStarHosts()} className="text-xs sm:text-sm" data-testid="agency-tab-star">
            ⭐ <span className="hidden sm:inline">Star Hosts</span><span className="sm:hidden">Star</span>
          </TabsTrigger>
          <TabsTrigger value="events" className="text-xs sm:text-sm">
            🎯 <span className="hidden sm:inline">Eventos</span>
          </TabsTrigger>
          <TabsTrigger value="exclusions" className="text-xs sm:text-sm">
            ⛔ <span className="hidden sm:inline">Exclusões</span>
          </TabsTrigger>
          <TabsTrigger value="notices" className="text-xs sm:text-sm" data-testid="agency-tab-notices">
            📢 <span className="hidden sm:inline">Avisos</span>
          </TabsTrigger>
        </TabsList>

        {/* Aba Avisos para Hosts */}
        <TabsContent value="notices" className="mt-4">
          <HostNoticesManagement />
        </TabsContent>

        {/* Aba Lista de Membros */}
        <TabsContent value="members" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <CardTitle className="text-lg sm:text-xl">
                  Membros ({allMembers.length})
                  {searchTerm && ` - Filtrados: ${filteredMembers.length}`}
                </CardTitle>
                
                <Input
                  placeholder="Buscar por ID ou nome..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full sm:w-64 text-sm"
                />
              </div>
            </CardHeader>
            
            <CardContent>
              {loading ? (
                <div className="flex justify-center p-8">
                  <FoxSpinnerInline size="md" />
                </div>
              ) : filteredMembers.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Nenhum membro encontrado</p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100 border-b-2">
                        <tr>
                          <th className="text-left p-2 sm:p-3 font-semibold">Avatar</th>
                          <th className="text-left p-2 sm:p-3 font-semibold">Nickname</th>
                          <th className="text-left p-2 sm:p-3 font-semibold">ID</th>
                          <th className="text-center p-2 sm:p-3 font-semibold">Level</th>
                          <th className="text-center p-2 sm:p-3 font-semibold">VIP</th>
                          <th className="text-center p-2 sm:p-3 font-semibold">Status</th>
                          <th className="text-left p-2 sm:p-3 font-semibold">Localização</th>
                          <th className="text-left p-2 sm:p-3 font-semibold">Link de Live</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredMembers.map((member) => (
                          <tr key={member.userId} className="border-b hover:bg-gray-50">
                            <td className="p-2 sm:p-3">
                              <div className="relative w-10 h-10 sm:w-12 sm:h-12">
                                <img
                                  src={member.avatar}
                                  alt={member.nickname}
                                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-full"
                                />
                                {member.avatarBorder && (
                                  <img
                                    src={member.avatarBorder}
                                    alt="Border"
                                    className="absolute inset-0 w-10 h-10 sm:w-12 sm:h-12 pointer-events-none"
                                  />
                                )}
                              </div>
                            </td>
                            
                            <td className="p-2 sm:p-3">
                              <p className="font-semibold text-xs sm:text-sm">{member.nickname}</p>
                              {member.signature && (
                                <p className="text-[10px] sm:text-xs text-gray-500 line-clamp-1">
                                  {member.signature}
                                </p>
                              )}
                            </td>
                            
                            <td className="p-2 sm:p-3">
                              <p className="font-mono text-xs">{member.showId}</p>
                            </td>
                            
                            <td className="p-2 sm:p-3 text-center">
                              <span className="text-xs sm:text-sm">Nv. {member.level}</span>
                            </td>
                            
                            <td className="p-2 sm:p-3 text-center">
                              {member.vipLevel > 0 ? (
                                <span className="text-yellow-600 font-semibold text-xs sm:text-sm">
                                  VIP {member.vipLevel}
                                </span>
                              ) : (
                                <span className="text-gray-400 text-xs">-</span>
                              )}
                            </td>
                            
                            <td className="p-2 sm:p-3 text-center">
                              <div className="flex flex-col gap-0.5 items-center text-xs sm:text-sm">
                                {member.isLiving && (
                                  <span className="text-red-600 font-semibold">🔴 AO VIVO</span>
                                )}
                                {member.certificate === 1 && (
                                  <span className="text-blue-600">✓ Verificado</span>
                                )}
                                {member.isNewAnchor && (
                                  <span className="text-green-600">✨ Novo</span>
                                )}
                                {!member.isLiving && member.certificate !== 1 && !member.isNewAnchor && (
                                  <span className="text-gray-400">-</span>
                                )}
                              </div>
                            </td>
                            
                            <td className="p-2 sm:p-3">
                              <p className="text-xs text-gray-600">
                                {member.country && `${member.country}`}
                                {member.area && ` - ${member.area}`}
                              </p>
                            </td>
                            
                            <td className="p-2 sm:p-3">
                              <div className="flex items-center gap-1">
                                {liveLinks[member.showId] ? (
                                  <>
                                    <a 
                                      href={liveLinks[member.showId]} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-xs text-blue-600 hover:underline truncate max-w-[100px]"
                                      title={liveLinks[member.showId]}
                                    >
                                      {liveLinks[member.showId]}
                                    </a>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => setEditingLink({ ...editingLink, [member.showId]: liveLinks[member.showId] })}
                                      className="h-6 w-6 p-0"
                                    >
                                      ✏️
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDeleteLiveLink(member.showId)}
                                      className="h-6 w-6 p-0 text-red-600"
                                    >
                                      🗑️
                                    </Button>
                                  </>
                                ) : editingLink[member.showId] !== undefined ? (
                                  <>
                                    <Input
                                      placeholder="https://..."
                                      value={editingLink[member.showId]}
                                      onChange={(e) => setEditingLink({ ...editingLink, [member.showId]: e.target.value })}
                                      className="h-8 text-xs"
                                    />
                                    <Button
                                      size="sm"
                                      onClick={() => handleSaveLiveLink(member.showId, editingLink[member.showId])}
                                      className="h-8 px-2 text-xs"
                                    >
                                      ✓
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        const newEditing = { ...editingLink };
                                        delete newEditing[member.showId];
                                        setEditingLink(newEditing);
                                      }}
                                      className="h-8 px-2 text-xs"
                                    >
                                      ✕
                                    </Button>
                                  </>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setEditingLink({ ...editingLink, [member.showId]: '' })}
                                    className="h-8 px-2 text-xs"
                                  >
                                    + Adicionar Link
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-4 text-center text-sm text-gray-600">
                    Exibindo {filteredMembers.length} de {allMembers.length} membros
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Aba Hosts Ao Vivo */}
        <TabsContent value="live" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div>
                  <CardTitle className="text-lg sm:text-xl">🔴 Hosts Ao Vivo</CardTitle>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">
                    Atualizado a cada 30 segundos
                  </p>
                </div>
                
                <div className="flex gap-2 items-center flex-wrap">
                  {/* Switch para mostrar barra no cliente */}
                  <label className="flex items-center gap-2 text-xs sm:text-sm bg-purple-50 border border-purple-300 rounded px-3 py-2">
                    <input
                      type="checkbox"
                      checked={showLiveBar}
                      onChange={(e) => handleToggleLiveBar(e.target.checked)}
                      className="w-4 h-4"
                    />
                    <span className="font-medium">Mostrar barra no cliente</span>
                  </label>
                  
                  <label className="flex items-center gap-2 text-xs sm:text-sm">
                    <input
                      type="checkbox"
                      checked={autoRefresh}
                      onChange={(e) => setAutoRefresh(e.target.checked)}
                      className="w-4 h-4"
                    />
                    Auto-refresh
                  </label>
                  <Button
                    onClick={loadLiveHosts}
                    disabled={loadingLive}
                    size="sm"
                  >
                    {loadingLive ? 'Atualizando...' : '🔄 Atualizar'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            
            <CardContent>
              {loadingLive && liveHosts.length === 0 ? (
                <div className="flex justify-center p-8">
                  <FoxSpinnerInline size="md" />
                  <p className="ml-3 text-sm text-gray-600">Buscando hosts ao vivo...</p>
                </div>
              ) : liveHosts.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">Nenhum host ao vivo no momento</p>
                  <p className="text-xs text-gray-400 mt-2">A lista é atualizada automaticamente</p>
                </div>
              ) : (
                <>
                  {/* Legenda de prioridade */}
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800 font-medium">📊 Ordem de Exibição - Arraste para Reordenar</p>
                    <p className="text-xs text-blue-600 mt-1">
                      Arraste os hosts para definir a ordem de exibição para o público. 
                      O primeiro da lista aparece primeiro para os clientes.
                    </p>
                  </div>
                  
                  {/* Lista de Hosts Ao Vivo com Drag and Drop */}
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={liveHosts
                        .sort((a, b) => (livePriorities[a.showId] || 9999) - (livePriorities[b.showId] || 9999))
                        .map(h => h.showId)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-2">
                        {liveHosts
                          .sort((a, b) => (livePriorities[a.showId] || 9999) - (livePriorities[b.showId] || 9999))
                          .map((host, index) => (
                            <SortableHostItem
                              key={host.showId}
                              host={host}
                              position={index + 1}
                              liveLinks={liveLinks}
                              livePriorities={livePriorities}
                              editingPriority={editingPriority}
                              setEditingPriority={setEditingPriority}
                              handleSavePriority={handleSavePriority}
                              Input={Input}
                            />
                          ))}
                      </div>
                    </SortableContext>
                  </DndContext>

                  <div className="mt-4 text-center">
                    <p className="text-sm text-gray-600">
                      {liveHosts.length} host(s) transmitindo ao vivo
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Última atualização: {new Date().toLocaleTimeString('pt-BR')}
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba Dados */}
        <TabsContent value="data" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                  <div>
                    <CardTitle className="text-lg sm:text-xl">Dados de Performance</CardTitle>
                    {dataDateRange && (
                      <p className="text-xs sm:text-sm text-gray-600 mt-1">Período: {dataDateRange}</p>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={() => loadHostsData('today')}
                      variant={dataPeriod === 'today' ? 'default' : 'outline'}
                      size="sm"
                    >
                      Hoje
                    </Button>
                    <Button
                      onClick={() => loadHostsData('yesterday')}
                      variant={dataPeriod === 'yesterday' ? 'default' : 'outline'}
                      size="sm"
                    >
                      Ontem
                    </Button>
                    <Button
                      onClick={() => loadHostsData('week')}
                      variant={dataPeriod === 'week' ? 'default' : 'outline'}
                      size="sm"
                    >
                      Esta Semana
                    </Button>
                  </div>
                </div>
                
                {/* Filtro de Período Customizado */}
                <div className="flex flex-col sm:flex-row gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex flex-col gap-1 flex-1">
                    <label className="text-xs font-semibold text-gray-700">Data Inicial:</label>
                    <Input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      min={getMinDate()}
                      max={getMaxDate()}
                      className="text-sm"
                    />
                  </div>
                  
                  <div className="flex flex-col gap-1 flex-1">
                    <label className="text-xs font-semibold text-gray-700">Data Final:</label>
                    <Input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      min={getMinDate()}
                      max={getMaxDate()}
                      className="text-sm"
                    />
                  </div>
                  
                  <div className="flex items-end">
                    <Button
                      onClick={loadCustomPeriodData}
                      disabled={loadingCustomData || !customStartDate || !customEndDate}
                      variant={dataPeriod === 'custom' ? 'default' : 'outline'}
                      size="sm"
                      className="w-full sm:w-auto"
                    >
                      {loadingCustomData ? 'Buscando...' : '🔍 Buscar Período'}
                    </Button>
                  </div>
                </div>
              </div>
            </CardHeader>
            
            <CardContent>
              {loadingData ? (
                <div className="flex justify-center p-8">
                  <FoxSpinnerInline size="md" />
                  <p className="ml-3 text-sm text-gray-600">Carregando dados...</p>
                </div>
              ) : hostsData.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Clique em "Esta Semana" ou "Hoje" para carregar dados</p>
              ) : (
                <>
                  {/* Dashboard */}
                  {dataSummary && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 mb-6">
                      <Card className="bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-300">
                        <CardContent className="p-4">
                          <p className="text-xs sm:text-sm text-gray-600 mb-1">🎁 Presente da Sorte</p>
                          <p className="text-xl sm:text-2xl font-bold text-yellow-700">
                            {formatDiamonds(dataSummary.luck_diamond)} 💎
                          </p>
                        </CardContent>
                      </Card>
                      
                      <Card className="bg-gradient-to-br from-pink-50 to-purple-50 border-pink-300">
                        <CardContent className="p-4">
                          <p className="text-xs sm:text-sm text-gray-600 mb-1">🎀 Presente Exclusivo</p>
                          <p className="text-xl sm:text-2xl font-bold text-pink-700">
                            {formatDiamonds(dataSummary.gift_diamond)} 💎
                          </p>
                        </CardContent>
                      </Card>
                      
                      <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-300">
                        <CardContent className="p-4">
                          <p className="text-xs sm:text-sm text-gray-600 mb-1">👥 Hosts Válidos</p>
                          <p className="text-xl sm:text-2xl font-bold text-green-700">
                            {dataSummary.valid_anchor_count} hosts
                          </p>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                  
                  {/* Botão Exportar */}
                  <div className="mb-4 flex justify-end">
                    <Button
                      onClick={handleExportData}
                      variant="outline"
                      size="sm"
                      className="bg-green-50 hover:bg-green-100 border-green-500 text-green-700"
                    >
                      📊 Exportar para Excel
                    </Button>
                  </div>
                  
                  {/* Tabela */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100 border-b-2">
                        <tr>
                          <th className="text-left p-2 sm:p-3 font-semibold">Host</th>
                          <th className="text-right p-2 sm:p-3 font-semibold">Presentes da Sorte</th>
                          <th className="text-right p-2 sm:p-3 font-semibold">Presentes Exclusivos</th>
                          <th className="text-right p-2 sm:p-3 font-semibold">Renda Host</th>
                          <th className="text-right p-2 sm:p-3 font-semibold">Renda Agência</th>
                          <th className="text-center p-2 sm:p-3 font-semibold">Tempo Live</th>
                          <th className="text-center p-2 sm:p-3 font-semibold">Dias</th>
                        </tr>
                      </thead>
                      <tbody>
                        {hostsData.map((host) => (
                          <tr key={host.user_id} className="border-b hover:bg-gray-50">
                            <td className="p-2 sm:p-3">
                              <div className="flex items-center gap-2">
                                <img
                                  src={host.avatar}
                                  alt={host.nickname}
                                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-full"
                                />
                                <div>
                                  <p className="font-semibold text-xs sm:text-sm">{host.nickname}</p>
                                  <p className="text-[10px] sm:text-xs text-gray-500">{host.show_id}</p>
                                </div>
                              </div>
                            </td>
                            
                            <td className="p-2 sm:p-3 text-right">
                              <p className="font-mono text-xs sm:text-sm text-yellow-600 font-semibold">
                                {formatDiamonds(host.luck_diamond)} 🎁
                              </p>
                            </td>
                            
                            <td className="p-2 sm:p-3 text-right">
                              <p className="font-mono text-xs sm:text-sm text-pink-600 font-semibold">
                                {formatDiamonds(host.gift_diamond)} 🎀
                              </p>
                            </td>
                            
                            <td className="p-2 sm:p-3 text-right">
                              <p className="font-mono text-xs sm:text-sm text-green-600 font-semibold">
                                {formatDiamonds(host.anchor_income)}
                              </p>
                            </td>
                            
                            <td className="p-2 sm:p-3 text-right">
                              <p className="font-mono text-xs sm:text-sm text-purple-600 font-semibold">
                                {formatDiamonds(host.agency_income)}
                              </p>
                            </td>
                            
                            <td className="p-2 sm:p-3 text-center">
                              <p className="text-xs sm:text-sm">
                                {formatMinutes(host.valid_duration)}
                              </p>
                            </td>
                            
                            <td className="p-2 sm:p-3 text-center">
                              <p className="text-xs sm:text-sm">{host.live_days}</p>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="mt-4 text-center text-sm text-gray-600">
                    Total: {hostsData.length} hosts
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>


        {/* Aba Eventos de Hosts */}
        {/* Aba Star Hosts */}
        <TabsContent value="star" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-base sm:text-lg">
                  ⭐ Acompanhamento de Star Hosts
                  {starHosts?.period_start && (
                    <span className="text-xs font-normal text-gray-500 ml-2">ciclo {starHosts.period_start} — {starHosts.period_end}</span>
                  )}
                </CardTitle>
                <Button size="sm" variant="outline" onClick={() => loadStarHosts(true)} disabled={loadingStarHosts} data-testid="star-hosts-refresh">
                  {loadingStarHosts ? 'Atualizando...' : '🔄 Atualizar'}
                </Button>
              </div>
              {starHosts?.cycles_seen?.length > 0 && (
                <p className="mt-2 text-[11px] text-gray-500" data-testid="star-hosts-cycles-seen">
                  Ciclos informados pela KAKO:{' '}
                  {starHosts.cycles_seen.map((c) => (
                    `${c.start}–${c.end} (${c.days}d${c.month_days ? ` · mês de ${c.month_days}` : ''})`
                  )).join(' · ')}
                </p>
              )}
            </CardHeader>
            <CardContent>
              {loadingStarHosts && !starHosts ? (
                <div className="flex justify-center p-8"><FoxSpinnerInline size="lg" /></div>
              ) : !starHosts ? (
                <p className="text-sm text-gray-500 text-center py-6">Clique em Atualizar para carregar.</p>
              ) : (
                <div className="space-y-4">
                  {/* Resumo */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                    {[
                      ['Ativos', starHosts.summary.active, 'text-emerald-600'],
                      ['Pausados', starHosts.summary.paused, 'text-amber-600'],
                      ['Risco de perder', starHosts.summary.at_risk, 'text-rose-600'],
                      ['Perto de subir', starHosts.summary.near_promotion, 'text-blue-600'],
                      ['Meta cumprida', starHosts.summary.on_track, 'text-emerald-600'],
                      [`Dias restantes`, starHosts.days_left, 'text-gray-700'],
                    ].map(([label, value, color]) => (
                      <div key={label} className="bg-gray-50 border rounded-lg p-2.5 text-center">
                        <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">{label}</p>
                        <p className={`text-lg font-bold ${color}`}>{value}</p>
                      </div>
                    ))}
                  </div>

                  {/* Filtros */}
                  <div className="flex flex-wrap gap-2">
                    {[
                      ['all', `Todos (${starHosts.hosts.length})`],
                      ['danger', `Risco de perder (${starHosts.summary.at_risk})`],
                      ['near_promotion', `Perto de subir (${starHosts.summary.near_promotion})`],
                      ['paused', `Pausados (${starHosts.summary.paused})`],
                      ['safe', `Já cumpriram (${starHosts.summary.on_track})`],
                    ].map(([key, label]) => (
                      <button key={key} type="button" onClick={() => setStarFilter(key)}
                        data-testid={`star-hosts-filter-${key}`}
                        className={`rounded-full border px-3 py-1 text-[11px] font-semibold transition-colors ${
                          starFilter === key
                            ? 'border-orange-500 bg-orange-500 text-white'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-orange-300'}`}>
                        {label}
                      </button>
                    ))}
                  </div>

                  {/* Tabela */}
                  <div className="overflow-x-auto max-h-[500px] overflow-y-auto pr-2">
                    <table className="w-full text-xs sm:text-sm" data-testid="star-hosts-table">
                      <thead className="sticky top-0 bg-white shadow-sm">
                        <tr className="text-left text-gray-500 border-b">
                          <th className="py-2 pr-2">Host</th>
                          <th className="py-2 pr-2">Nível</th>
                          <th className="py-2 pr-2">Situação</th>
                          <th className="py-2 pr-2 text-right">Dias válidos</th>
                          <th className="py-2 pr-2 text-right">Ganhos 💎</th>
                          <th className="py-2 pr-2 text-right">Média/dia 💎</th>
                          <th className="py-2 min-w-[160px]">Meta do ciclo</th>
                          <th className="py-2 min-w-[150px]">Ritmo p/ subir</th>
                        </tr>
                      </thead>
                      <tbody>
                        {starHosts.hosts
                          .filter((h) => starFilter === 'all'
                            || (starFilter === 'near_promotion' ? h.near_promotion : h.risk === starFilter))
                          .map((h) => (
                          <tr key={h.kako_id} className="border-b hover:bg-gray-50" data-testid={`star-host-row-${h.kako_id}`}>
                            <td className="py-2 pr-2">
                              <div className="flex items-center gap-2 min-w-[140px]">
                                {h.avatar
                                  ? <img src={h.avatar} alt="" className="w-7 h-7 rounded-full object-cover" />
                                  : <div className="w-7 h-7 rounded-full bg-gray-200" />}
                                <div className="min-w-0">
                                  <p className="font-medium truncate max-w-[130px]">{h.nickname}</p>
                                  <p className="text-[10px] text-gray-400">{h.kako_id}</p>
                                </div>
                              </div>
                            </td>
                            <td className="py-2 pr-2">
                              <Badge className="bg-gradient-to-r from-yellow-400 to-amber-500 text-amber-950 font-bold">{h.grade}</Badge>
                            </td>
                            <td className="py-2 pr-2 min-w-[150px]">
                              <Badge className={
                                h.risk === 'danger' ? 'bg-rose-100 text-rose-700 border border-rose-200'
                                  : h.risk === 'warning' ? 'bg-amber-100 text-amber-700 border border-amber-200'
                                    : h.risk === 'paused' ? 'bg-gray-100 text-gray-600 border border-gray-200'
                                      : 'bg-emerald-100 text-emerald-700 border border-emerald-200'}>
                                {h.risk === 'danger' ? '🔴 Risco de perder'
                                  : h.risk === 'warning' ? '🟡 Atenção'
                                    : h.risk === 'paused' ? '⏸ Pausado' : '🟢 Seguro'}
                              </Badge>
                              <p className="text-[10px] text-gray-400 mt-0.5">{h.risk_label}</p>
                              {h.near_promotion && (
                                <p className="text-[10px] font-semibold text-blue-600 mt-0.5" data-testid={`star-host-near-promo-${h.kako_id}`}>
                                  🚀 perto de subir para {h.next_level}
                                </p>
                              )}
                            </td>
                            <td className="py-2 pr-2 text-right">
                              <span className={h.valid_days_missing > 0 ? 'text-rose-600 font-semibold' : 'text-emerald-600 font-semibold'}>
                                {h.valid_days}
                              </span>
                              <span className="text-gray-400">/{h.min_valid_days}</span>
                            </td>
                            <td className="py-2 pr-2 text-right font-semibold">{h.gift_income.toLocaleString('pt-BR')}</td>
                            <td className="py-2 pr-2 text-right">{h.avg_gift_income.toLocaleString('pt-BR')}</td>
                            <td className="py-2">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden min-w-[70px]">
                                  <div className={`h-full rounded-full ${h.meta_atingida ? 'bg-emerald-500'
                                    : h.risk === 'danger' ? 'bg-rose-500' : 'bg-amber-400'}`}
                                    style={{ width: `${h.income_pct}%` }} />
                                </div>
                                <span className={`text-[11px] font-bold whitespace-nowrap ${h.meta_atingida ? 'text-emerald-600' : 'text-amber-600'}`}>
                                  {h.income_pct}% {h.meta_atingida ? '✅' : ''}
                                </span>
                              </div>
                              <p className="text-[10px] text-gray-400">
                                meta 💎 {h.income_target.toLocaleString('pt-BR')}
                                {h.income_remaining > 0 && ` · faltam ${h.income_remaining.toLocaleString('pt-BR')}`}
                              </p>
                            </td>
                            <td className="py-2">
                              {h.promo_pace_target > 0 ? (
                                <>
                                  <div className="flex items-center gap-2">
                                    <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden min-w-[60px]">
                                      <div className={`h-full rounded-full ${h.near_promotion ? 'bg-blue-500' : 'bg-blue-200'}`}
                                        style={{ width: `${h.promo_pace_pct}%` }} />
                                    </div>
                                    <span className="text-[11px] font-bold text-blue-600 whitespace-nowrap">{h.promo_pace_pct}%</span>
                                  </div>
                                  <p className="text-[10px] text-gray-400">
                                    {h.status === 'paused' ? 'reativar p/ A' : `ritmo p/ ${h.next_level}`}
                                    {' '}· 💎 {h.promo_pace_target.toLocaleString('pt-BR')} no ciclo
                                  </p>
                                </>
                              ) : (
                                <span className="text-[11px] text-gray-400">nível máximo</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <p className="text-[11px] text-gray-500 leading-relaxed">
                    Dias válidos e comissão vêm da consulta de Star Host da KAKO — ela já aplica a hora mínima de
                    cada host e a isenção de 1h. Meta do ciclo = média por dia do nível × dias válidos (mínimo de 10).
                    O ritmo para subir compara a comissão do ciclo com metade da meta de 30 dias, que é a janela de promoção.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba Eventos */}
        <TabsContent value="events" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg sm:text-xl">🎯 Eventos de Hosts</CardTitle>
                <div className="flex gap-2">
                  <Button
                    onClick={handleRefinalizePreviousWeek}
                    size="sm"
                    variant="outline"
                    className="bg-orange-50 hover:bg-orange-100 border-orange-500 text-orange-700"
                  >
                    🔄 Refinilizar Semana Passada
                  </Button>
                  {workEventConfig && (
                    <label className="flex items-center gap-2 text-sm bg-purple-50 border border-purple-300 rounded px-3 py-2">
                      <input
                        type="checkbox"
                        checked={workEventConfig.active}
                        onChange={(e) => handleToggleWorkEvent(e.target.checked)}
                        className="w-4 h-4"
                      />
                      <span className="font-medium">Evento Ativo</span>
                    </label>
                  )}
                </div>
              </div>
            </CardHeader>
            
            <CardContent>
              <Tabs defaultValue="trabalhou-ganhou" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="trabalhou-ganhou" onClick={() => workEventConfig?.active && loadWorkEventHosts()}>
                    💼 Trabalhou Ganhou
                  </TabsTrigger>
                  <TabsTrigger value="ranking-estrelas" onClick={() => starRankingConfig?.active && loadStarRanking()}>
                    ⭐ Ranking de Estrelas
                  </TabsTrigger>
                </TabsList>
                
                {/* Sub-aba: Trabalhou Ganhou */}
                <TabsContent value="trabalhou-ganhou" className="mt-4 space-y-4">
                  {!workEventConfig?.active ? (
                    <div className="text-center py-8 text-gray-500">
                      Evento desativado. Ative o switch acima para começar.
                    </div>
                  ) : (
                    <>
                      {/* Regras do Evento */}
                      <Card className="bg-gradient-to-br from-purple-50 to-blue-50 border-purple-200">
                        <CardHeader>
                          <CardTitle className="text-base sm:text-lg text-purple-900">📋 Regras do Evento</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3 text-sm">
                          <p className="text-gray-700">
                            As quantidades de pontos obtidos são determinadas pelo valor do presente da sorte recebido do dia e a duração de live.
                          </p>
                          
                          {/* Tabela de Pontos Diários */}
                          <div>
                            <h3 className="font-bold text-purple-800 mb-2">Tabela de Pontos Diários:</h3>
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs sm:text-sm border-collapse">
                                <thead>
                                  <tr className="bg-purple-600 text-white">
                                    <th className="p-2 text-left border border-purple-400">Meta concluída do dia</th>
                                    <th className="p-2 text-center border border-purple-400">Duração de live do dia</th>
                                    <th className="p-2 text-center border border-purple-400">Pontos obtidos</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {[
                                    [0, '2H', 0.3],
                                    [150000, '2H', 0.4],
                                    [300000, '2H', 0.5],
                                    [600000, '2H', 1],
                                    [1000000, '2H', 2],
                                    [1500000, '2H', 3],
                                    [3000000, '2H', 4],
                                    [5000000, '2H', 5],
                                    [8000000, '2H', 6],
                                    [12000000, '2H', 7],
                                    [20000000, '2H', 8],
                                    [30000000, '2H', 9],
                                    [50000000, '2H', 10],
                                    [100000000, '2H', 11],
                                  ].map((row, idx) => (
                                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-purple-50'}>
                                      <td className="p-2 border border-gray-300">{row[0].toLocaleString('pt-BR')}</td>
                                      <td className="p-2 text-center border border-gray-300">{row[1]}</td>
                                      <td className="p-2 text-center border border-gray-300 text-purple-700 font-bold">{row[2]}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                          
                          <p className="text-gray-600 italic">
                            No final, os pontos obtidos serão determinados pelo número de dias de presença na semana.
                          </p>
                          
                          {/* Tabela de Presença Semanal */}
                          <div>
                            <h3 className="font-bold text-purple-800 mb-2">Presença Semanal:</h3>
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs sm:text-sm border-collapse">
                                <thead>
                                  <tr className="bg-purple-600 text-white">
                                    <th className="p-2 text-left border border-purple-400">Presença da semana</th>
                                    <th className="p-2 text-center border border-purple-400">Múltiplo</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {[
                                    ['4 dias', 'x1'],
                                    ['5 dias', 'x1.5'],
                                    ['6 dias', 'x1.7'],
                                    ['7 dias', 'x2'],
                                  ].map((row, idx) => (
                                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-purple-50'}>
                                      <td className="p-2 border border-gray-300">{row[0]}</td>
                                      <td className="p-2 text-center border border-gray-300 text-purple-700 font-bold">{row[1]}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                          
                          {/* Sobre os pontos */}
                          <div className="bg-blue-50 p-3 rounded border border-blue-200">
                            <h3 className="font-bold text-blue-800 mb-1">💎 Sobre os pontos:</h3>
                            <p>Cada ponto pode ser trocado por <span className="font-bold text-blue-900">2000 cristais</span></p>
                            <p className="text-xs mt-1">Cristais ganhos = Pontos × Múltiplo × 2000</p>
                          </div>
                          
                          {/* Sobre o evento */}
                          <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                            <h3 className="font-bold text-yellow-800 mb-2">⚠️ Sobre o evento:</h3>
                            <ol className="text-xs space-y-1 pl-4">
                              <li>1. Os pontos são válidos apenas na mesma semana.</li>
                              <li>2. É necessário ter, no mínimo, 4 dias de presença na semana para receber a recompensa.</li>
                              <li>3. Cada semana natural tem um ciclo de evento completo.</li>
                              <li>4. Tempo de pagamento: Após o término de cada semana, o pagamento pode ser coletado manualmente após as 09:00 do primeiro dia útil da semana seguinte.</li>
                              <li>5. Caso seja detectado live com ausência do host, live com tela preta e etc., o direito de participar do evento será cancelado.</li>
                            </ol>
                          </div>
                        </CardContent>
                      </Card>
                      

                

                      
                      {/* Tabs Internas: Semana Atual + Histórico + Configuração */}
                      <Tabs defaultValue="semana-atual" className="w-full">
                        <TabsList className="grid w-full grid-cols-4">
                          <TabsTrigger value="semana-atual">
                            📊 Semana Atual
                          </TabsTrigger>
                          <TabsTrigger value="historico-resgates" onClick={() => loadWorkEventHistory()}>
                            📜 Histórico
                          </TabsTrigger>
                          <TabsTrigger value="recompensas-perdidas" onClick={() => loadWorkEventExpired()}>
                            ❌ Perdidas
                          </TabsTrigger>
                          <TabsTrigger value="configuracao" onClick={() => loadWorkEventSettings()}>
                            ⚙️ Configuração
                          </TabsTrigger>
                        </TabsList>
                        
                        {/* Sub-aba: Semana Atual */}
                        <TabsContent value="semana-atual" className="mt-4 space-y-4">

                {/* Sub-aba: Histórico de Resgates */}

                      {/* Controles Admin */}
                      <div className="flex gap-2 justify-end">
                        <Button
                          onClick={handleSyncWorkEventData}
                          disabled={syncingWorkEvent}
                          size="sm"
                          variant="outline"
                        >
                          {syncingWorkEvent ? '⏳ Sincronizando...' : '🔄 Sincronizar Dados de Hoje'}
                        </Button>
                      </div>
                      
                      {/* Lista de Hosts */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base sm:text-lg">Hosts da Semana Atual</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {loadingWorkEvent ? (
                            <div className="flex justify-center p-8">
                              <FoxSpinnerInline size="md" />
                            </div>
                          ) : workEventHosts.length === 0 ? (
                            <p className="text-center text-gray-500 py-8">Clique em "Sincronizar Dados" para carregar</p>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead className="bg-purple-100 border-b-2">
                                  <tr>
                                    <th className="text-left p-2 sm:p-3 font-semibold">Host</th>
                                    <th className="text-center p-2 sm:p-3 font-semibold">Pontos</th>
                                    <th className="text-center p-2 sm:p-3 font-semibold">Dias Válidos</th>
                                    <th className="text-center p-2 sm:p-3 font-semibold">Multiplicador</th>
                                    <th className="text-right p-2 sm:p-3 font-semibold">Cristais Estimados</th>
                                    <th className="text-center p-2 sm:p-3 font-semibold">Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {workEventHosts.map((host) => (
                                    <tr key={host.show_id} className="border-b hover:bg-gray-50">
                                      <td className="p-2 sm:p-3">
                                        <div className="flex items-center gap-2">
                                          <img
                                            src={host.avatar}
                                            alt={host.nickname}
                                            className="w-8 h-8 sm:w-10 sm:h-10 rounded-full"
                                          />
                                          <div>
                                            <p className="font-semibold text-xs sm:text-sm">{host.nickname}</p>
                                            <p className="text-[10px] sm:text-xs text-gray-500">{host.show_id}</p>
                                          </div>
                                        </div>
                                      </td>
                                      <td className="p-2 sm:p-3 text-center">
                                        <p className="font-bold text-purple-700">{host.total_points.toFixed(1)}</p>
                                      </td>
                                      <td className="p-2 sm:p-3 text-center">
                                        <p className={host.valid_days >= 4 ? 'text-green-600 font-semibold' : 'text-gray-500'}>
                                          {host.valid_days}/7
                                        </p>
                                      </td>
                                      <td className="p-2 sm:p-3 text-center">
                                        <p className="font-bold text-blue-700">x{host.multiplier}</p>
                                      </td>
                                      <td className="p-2 sm:p-3 text-right">
                                        <p className="font-mono font-bold text-green-600">
                                          {host.crystals_estimated.toLocaleString('pt-BR')} 💎
                                        </p>
                                      </td>
                                      <td className="p-2 sm:p-3 text-center">
                                        {host.is_eligible ? (
                                          <Badge className="bg-green-600">✓ Elegível</Badge>
                                        ) : (
                                          <Badge variant="outline" className="text-gray-500">
                                            Min. 4 dias
                                          </Badge>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              
                              <div className="mt-4 text-center text-sm text-gray-600">
                                Total: {workEventHosts.length} hosts
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>

                

                        </TabsContent>
                        
                        {/* Sub-aba: Histórico de Resgates */}
                        <TabsContent value="historico-resgates" className="mt-4">
                          <Card>
                            <CardHeader>
                              <CardTitle className="text-base sm:text-lg">📜 Histórico de Resgates Semanais</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                              {/* Filtros e Ordenação — modelo Tarefas VIP */}
                              <div className="grid gap-4 md:grid-cols-5">
                                <div className="space-y-2">
                                  <Label className="text-sm">Show ID (Host)</Label>
                                  <Input
                                    placeholder="Filtrar por host"
                                    value={workEventFilter}
                                    onChange={(e) => setWorkEventFilter(e.target.value)}
                                    data-testid="work-event-filter-input"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label className="text-sm">Data Início</Label>
                                  <Input
                                    type="date"
                                    value={workEventDateFilter.start_date}
                                    onChange={(e) => setWorkEventDateFilter({ ...workEventDateFilter, start_date: e.target.value })}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label className="text-sm">Data Fim</Label>
                                  <Input
                                    type="date"
                                    value={workEventDateFilter.end_date}
                                    onChange={(e) => setWorkEventDateFilter({ ...workEventDateFilter, end_date: e.target.value })}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label className="text-sm">Ordenar por</Label>
                                  <select
                                    value={workEventSortBy}
                                    onChange={(e) => setWorkEventSortBy(e.target.value)}
                                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                                  >
                                    <option value="week_desc">Semana (Recente)</option>
                                    <option value="week_asc">Semana (Antiga)</option>
                                    <option value="coins_desc">Moedas (Maior)</option>
                                    <option value="coins_asc">Moedas (Menor)</option>
                                    <option value="rewards_desc">Resgates (Maior)</option>
                                    <option value="rewards_asc">Resgates (Menor)</option>
                                  </select>
                                </div>
                                <div className="space-y-2">
                                  <Label className="text-sm">&nbsp;</Label>
                                  <Button 
                                    onClick={() => loadWorkEventHistory(workEventFilter)}
                                    disabled={loadingHistory}
                                    className="w-full bg-green-600 hover:bg-green-700"
                                    data-testid="work-event-filter-btn"
                                  >
                                    {loadingHistory ? 'Buscando...' : 'Aplicar Filtros'}
                                  </Button>
                                </div>
                              </div>
                              
                              {loadingHistory ? (
                                <div className="flex justify-center p-8">
                                  <FoxSpinnerInline size="md" />
                                </div>
                              ) : workEventHistory.length === 0 ? (
                                <p className="text-center text-gray-500 py-8">Nenhum resgate realizado ainda</p>
                              ) : (
                                <div className="space-y-3">
                                  {(() => {
                                    // Filtros e ordenação client-side
                                    let filtered = [...workEventHistory];
                                    if (workEventDateFilter.start_date) {
                                      filtered = filtered.filter(w => w.week_start >= workEventDateFilter.start_date);
                                    }
                                    if (workEventDateFilter.end_date) {
                                      filtered = filtered.filter(w => w.week_end <= workEventDateFilter.end_date);
                                    }
                                    filtered.sort((a, b) => {
                                      switch (workEventSortBy) {
                                        case 'week_desc':    return (b.week_start || '').localeCompare(a.week_start || '');
                                        case 'week_asc':     return (a.week_start || '').localeCompare(b.week_start || '');
                                        case 'coins_desc':   return (b.total_coins || 0) - (a.total_coins || 0);
                                        case 'coins_asc':    return (a.total_coins || 0) - (b.total_coins || 0);
                                        case 'rewards_desc': return (b.rewards?.length || 0) - (a.rewards?.length || 0);
                                        case 'rewards_asc':  return (a.rewards?.length || 0) - (b.rewards?.length || 0);
                                        default: return 0;
                                      }
                                    });
                                    
                                    if (filtered.length === 0) {
                                      return <p className="text-center text-gray-500 py-8">Nenhum resgate no período filtrado</p>;
                                    }
                                    
                                    return filtered.map((weekGroup) => {
                                      const weekIds = weekGroup.rewards.map((r) => r.id);
                                      const weekAllSelected = weekIds.length > 0 && weekIds.every((id) => selectedWorkClaims.includes(id));
                                      const weekSomeSelected = weekIds.some((id) => selectedWorkClaims.includes(id));
                                      return (
                                      <div key={weekGroup.week_start} className="border rounded-lg p-3 bg-green-50 mb-3">
                                        <div className="flex items-center justify-between mb-3">
                                          <h4 className="font-bold text-sm text-green-700">
                                            📅 Semana: {weekGroup.week_start} a {weekGroup.week_end}
                                          </h4>
                                          <Badge className="bg-green-600 text-white">
                                            {weekGroup.rewards.length} resgate(s) • Total: {weekGroup.total_coins.toLocaleString('pt-BR')} 💎
                                          </Badge>
                                        </div>
                                        <div className="overflow-x-auto">
                                          <table className="w-full text-sm">
                                            <thead className="bg-gray-100">
                                              <tr>
                                                <th className="p-2 w-8">
                                                  <input
                                                    type="checkbox"
                                                    checked={weekAllSelected}
                                                    ref={(el) => { if (el) el.indeterminate = !weekAllSelected && weekSomeSelected; }}
                                                    onChange={(e) => toggleAllWorkClaims(weekGroup.rewards, e.target.checked)}
                                                    data-testid={`work-week-select-all-${weekGroup.week_start}`}
                                                  />
                                                </th>
                                                <th className="text-left p-2">Host</th>
                                                <th className="text-center p-2">Pontos</th>
                                                <th className="text-center p-2">Dias</th>
                                                <th className="text-center p-2">Mult.</th>
                                                <th className="text-right p-2">Moedas</th>
                                                <th className="text-center p-2">Resgatado em</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {weekGroup.rewards.map((reward) => (
                                                <tr key={reward.id} className="border-b hover:bg-white">
                                                  <td className="p-2">
                                                    <input
                                                      type="checkbox"
                                                      checked={selectedWorkClaims.includes(reward.id)}
                                                      onChange={() => toggleWorkClaim(reward.id)}
                                                      data-testid={`work-claim-checkbox-${reward.id}`}
                                                    />
                                                  </td>
                                                  <td className="p-2">
                                                    <div className="flex items-center gap-2">
                                                      {reward.avatar && (
                                                        <img src={reward.avatar} alt={reward.nickname} className="w-7 h-7 rounded-full border" />
                                                      )}
                                                      <div className="text-xs">
                                                        <div className="font-medium">{reward.nickname || 'Desconhecido'}</div>
                                                        <div className="text-gray-500">{reward.show_id}</div>
                                                      </div>
                                                    </div>
                                                  </td>
                                                  <td className="p-2 text-center">
                                                    <span className="font-bold text-purple-700">{(reward.total_points || 0).toFixed(1)}</span>
                                                  </td>
                                                  <td className="p-2 text-center text-xs">
                                                    <span className="text-green-600 font-semibold">{reward.valid_days}/7</span>
                                                  </td>
                                                  <td className="p-2 text-center text-xs">
                                                    <span className="font-bold text-blue-700">x{reward.multiplier}</span>
                                                  </td>
                                                  <td className="p-2 text-right">
                                                    <Badge className="bg-green-600 text-white text-xs">
                                                      +{(reward.crystals_earned || 0).toLocaleString('pt-BR')} 💎
                                                    </Badge>
                                                  </td>
                                                  <td className="p-2 text-center text-xs">
                                                    {reward.claimed_at ? new Date(reward.claimed_at).toLocaleString('pt-BR') : '-'}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                      );
                                    });
                                  })()}
                                  
                                  {/* Barra de ação para deletar selecionados */}
                                  {selectedWorkClaims.length > 0 && (
                                    <div className="sticky bottom-2 z-10 mt-3 p-3 bg-red-50 border border-red-300 rounded-lg flex items-center justify-between gap-2">
                                      <span className="text-sm text-red-700 font-semibold">
                                        {selectedWorkClaims.length} resgate(s) selecionado(s)
                                      </span>
                                      <div className="flex gap-2">
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => setSelectedWorkClaims([])}
                                        >
                                          Cancelar
                                        </Button>
                                        <Button
                                          size="sm"
                                          className="bg-red-600 hover:bg-red-700 text-white"
                                          disabled={deletingClaims}
                                          onClick={deleteSelectedWorkClaims}
                                          data-testid="work-delete-selected-btn"
                                        >
                                          {deletingClaims ? 'Apagando...' : '🗑️ Apagar selecionados'}
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Total Geral */}
                                  <div className="mt-4 p-3 bg-green-50 rounded-lg">
                                    <p className="text-center text-sm font-semibold text-green-700">
                                      Total Geral: {workEventHistory.reduce((sum, w) => sum + w.total_coins, 0).toLocaleString('pt-BR')} moedas em {workEventHistory.reduce((sum, w) => sum + w.rewards.length, 0)} resgates
                                    </p>
                                  </div>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        </TabsContent>
                        
                        {/* Sub-aba: Recompensas Perdidas - Trabalhou Ganhou */}
                        <TabsContent value="recompensas-perdidas" className="mt-4">
                          <Card>
                            <CardHeader>
                              <CardTitle className="text-base sm:text-lg text-red-600">❌ Recompensas Perdidas</CardTitle>
                              <p className="text-sm text-gray-500">Recompensas não resgatadas que expiraram (agrupadas por semana)</p>
                            </CardHeader>
                            <CardContent>
                              {/* Filtro por ID */}
                              <div className="flex gap-2 mb-4">
                                <Input
                                  placeholder="Filtrar por ID do host..."
                                  value={workExpiredFilter}
                                  onChange={(e) => setWorkExpiredFilter(e.target.value)}
                                  className="max-w-xs"
                                  data-testid="work-expired-filter-input"
                                />
                                <Button 
                                  onClick={() => loadWorkEventExpired(workExpiredFilter)}
                                  disabled={loadingWorkExpired}
                                  data-testid="work-expired-filter-btn"
                                >
                                  {loadingWorkExpired ? 'Buscando...' : 'Buscar'}
                                </Button>
                                {workExpiredFilter && (
                                  <Button 
                                    variant="outline"
                                    onClick={() => {
                                      setWorkExpiredFilter('');
                                      loadWorkEventExpired('');
                                    }}
                                  >
                                    Limpar
                                  </Button>
                                )}
                              </div>
                              
                              {loadingWorkExpired ? (
                                <div className="flex justify-center p-8">
                                  <FoxSpinnerInline size="md" />
                                </div>
                              ) : workEventExpired.length === 0 ? (
                                <p className="text-center text-gray-500 py-8">Nenhuma recompensa perdida encontrada</p>
                              ) : (
                                <div className="space-y-6">
                                  {/* Agrupar por semana */}
                                  {Object.entries(
                                    workEventExpired.reduce((groups, reward) => {
                                      const weekKey = `${reward.week_start} a ${reward.week_end}`;
                                      if (!groups[weekKey]) {
                                        groups[weekKey] = {
                                          week_start: reward.week_start,
                                          week_end: reward.week_end,
                                          rewards: [],
                                          total_crystals: 0
                                        };
                                      }
                                      groups[weekKey].rewards.push(reward);
                                      groups[weekKey].total_crystals += (reward.crystals_earned || 0);
                                      return groups;
                                    }, {})
                                  )
                                  .sort((a, b) => b[1].week_start.localeCompare(a[1].week_start))
                                  .map(([weekKey, weekData]) => (
                                    <div key={weekKey} className="border rounded-lg overflow-hidden">
                                      {/* Header da Semana */}
                                      <div className="bg-red-100 p-3 flex justify-between items-center">
                                        <div>
                                          <p className="font-bold text-red-800">📅 Semana: {weekData.week_start} a {weekData.week_end}</p>
                                          <p className="text-xs text-red-600">{weekData.rewards.length} recompensa(s) perdida(s)</p>
                                        </div>
                                        <div className="text-right">
                                          <p className="font-bold text-red-700 text-lg">
                                            {weekData.total_crystals.toLocaleString('pt-BR')} 💎
                                          </p>
                                          <p className="text-xs text-red-600">perdidas nesta semana</p>
                                        </div>
                                      </div>
                                      
                                      {/* Tabela de hosts da semana */}
                                      <table className="w-full text-sm">
                                        <thead className="bg-red-50 border-b">
                                          <tr>
                                            <th className="text-left p-2 sm:p-3 font-semibold">Host</th>
                                            <th className="text-center p-2 sm:p-3 font-semibold">Pontos</th>
                                            <th className="text-center p-2 sm:p-3 font-semibold">Dias</th>
                                            <th className="text-right p-2 sm:p-3 font-semibold">Moedas</th>
                                            <th className="text-center p-2 sm:p-3 font-semibold">Expirou</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {weekData.rewards.map((reward) => (
                                            <tr key={reward.id} className="border-b hover:bg-red-50">
                                              <td className="p-2 sm:p-3">
                                                <div className="flex items-center gap-2">
                                                  {reward.avatar && (
                                                    <img
                                                      src={reward.avatar}
                                                      alt={reward.nickname}
                                                      className="w-8 h-8 rounded-full"
                                                    />
                                                  )}
                                                  <div>
                                                    <p className="font-semibold text-xs sm:text-sm">{reward.nickname || 'Desconhecido'}</p>
                                                    <p className="text-[10px] text-gray-500">{reward.show_id}</p>
                                                  </div>
                                                </div>
                                              </td>
                                              <td className="p-2 sm:p-3 text-center">
                                                <p className="font-bold text-purple-700">{(reward.total_points || 0).toFixed(1)}</p>
                                              </td>
                                              <td className="p-2 sm:p-3 text-center">
                                                <p className="text-gray-600">{reward.valid_days || 0}/7</p>
                                              </td>
                                              <td className="p-2 sm:p-3 text-right">
                                                <p className="font-mono font-bold text-red-600">
                                                  {(reward.crystals_earned || 0).toLocaleString('pt-BR')} 💎
                                                </p>
                                              </td>
                                              <td className="p-2 sm:p-3 text-center">
                                                <p className="text-xs text-red-600">
                                                  {reward.expires_at ? new Date(reward.expires_at).toLocaleDateString('pt-BR') : '-'}
                                                </p>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  ))}
                                  
                                  {/* Total Geral */}
                                  <div className="mt-4 p-4 bg-red-100 rounded-lg border-2 border-red-300">
                                    <p className="text-center text-lg font-bold text-red-800">
                                      📊 Total Geral Perdido: {workEventExpired.reduce((sum, r) => sum + (r.crystals_earned || 0), 0).toLocaleString('pt-BR')} moedas
                                    </p>
                                    <p className="text-center text-sm text-red-600">
                                      {workEventExpired.length} recompensas em {Object.keys(workEventExpired.reduce((g, r) => { g[r.week_start] = 1; return g; }, {})).length} semana(s)
                                    </p>
                                  </div>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        </TabsContent>

                        {/* Sub-aba: Configuração do Evento */}
                        <TabsContent value="configuracao" className="mt-4">
                          <Card>
                            <CardHeader>
                              <div className="flex justify-between items-center">
                                <CardTitle className="text-base sm:text-lg">⚙️ Configuração do Evento</CardTitle>
                                <div className="flex gap-2">
                                  <Button
                                    onClick={handleResetWorkEventSettings}
                                    disabled={savingSettings}
                                    size="sm"
                                    variant="outline"
                                    className="bg-orange-50 hover:bg-orange-100 border-orange-500 text-orange-700"
                                  >
                                    🔄 Restaurar Padrão
                                  </Button>
                                  <Button
                                    onClick={handleSaveWorkEventSettings}
                                    disabled={savingSettings}
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700"
                                  >
                                    {savingSettings ? '⏳ Salvando...' : '💾 Salvar Configurações'}
                                  </Button>
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent className="space-y-6">
                              {loadingSettings ? (
                                <div className="flex justify-center p-8">
                                  <FoxSpinnerInline size="md" />
                                </div>
                              ) : (
                                <>
                                  {/* Multiplicador de Cristais */}
                                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                    <h3 className="font-bold text-blue-800 mb-3">💎 Multiplicador de Cristais</h3>
                                    <p className="text-sm text-gray-600 mb-2">Cristais = Pontos × Múltiplo de Presença × <strong>Multiplicador</strong></p>
                                    <div className="flex items-center gap-3">
                                      <label className="text-sm font-medium">Valor:</label>
                                      <input
                                        type="number"
                                        min="1"
                                        value={editedCrystalsMultiplier}
                                        onChange={(e) => setEditedCrystalsMultiplier(parseInt(e.target.value) || 1)}
                                        className="w-32 px-3 py-2 border rounded-lg text-center font-bold"
                                      />
                                      <span className="text-sm text-gray-500">(Padrão: 2000)</span>
                                    </div>
                                  </div>

                                  {/* Multiplicadores de Presença */}
                                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                                    <div className="flex justify-between items-center mb-3">
                                      <div>
                                        <h3 className="font-bold text-purple-800">📅 Multiplicadores de Presença Semanal</h3>
                                        <p className="text-sm text-gray-600">Defina o multiplicador para cada quantidade de dias válidos na semana</p>
                                      </div>
                                      <Button
                                        onClick={handleAddPresenceMultiplier}
                                        size="sm"
                                        className="bg-purple-600 hover:bg-purple-700"
                                      >
                                        + Adicionar Multiplicador
                                      </Button>
                                    </div>
                                    
                                    <div className="overflow-x-auto">
                                      <table className="w-full text-sm">
                                        <thead className="bg-purple-600 text-white">
                                          <tr>
                                            <th className="p-2 text-center">Dias de Presença</th>
                                            <th className="p-2 text-center">Multiplicador</th>
                                            <th className="p-2 text-center">Ações</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {Object.entries(editedPresenceMultiplier)
                                            .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
                                            .map(([days, multiplier], index) => (
                                            <tr key={days} className={index % 2 === 0 ? 'bg-white' : 'bg-purple-50'}>
                                              <td className="p-2">
                                                <input
                                                  type="number"
                                                  min="1"
                                                  max="7"
                                                  value={days}
                                                  onChange={(e) => handleUpdatePresenceDay(days, e.target.value)}
                                                  className="w-20 mx-auto px-2 py-1 border rounded text-center font-bold block"
                                                />
                                              </td>
                                              <td className="p-2">
                                                <div className="flex items-center justify-center gap-1">
                                                  <span className="text-purple-700 font-bold">x</span>
                                                  <input
                                                    type="number"
                                                    step="0.1"
                                                    min="0"
                                                    value={multiplier}
                                                    onChange={(e) => handleUpdatePresenceMultiplier(days, e.target.value)}
                                                    className="w-20 px-2 py-1 border rounded text-center font-bold"
                                                  />
                                                </div>
                                              </td>
                                              <td className="p-2 text-center">
                                                <Button
                                                  onClick={() => handleRemovePresenceMultiplier(days)}
                                                  size="sm"
                                                  variant="ghost"
                                                  className="text-red-600 hover:text-red-800 hover:bg-red-100"
                                                >
                                                  🗑️
                                                </Button>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                    
                                    <p className="text-xs text-gray-500 mt-3">
                                      Configure quantos dias de presença são necessários e qual o multiplicador de bônus.
                                      Ex: 4 dias = x1.0, 7 dias = x2.0 (dobra os pontos)
                                    </p>
                                  </div>

                                  {/* Tabela de Metas */}
                                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                    <div className="flex justify-between items-center mb-3">
                                      <div>
                                        <h3 className="font-bold text-green-800">🎯 Tabela de Metas e Pontos</h3>
                                        <p className="text-sm text-gray-600">Configure as metas diárias e pontos correspondentes</p>
                                      </div>
                                      <Button
                                        onClick={handleAddGoal}
                                        size="sm"
                                        className="bg-green-600 hover:bg-green-700"
                                      >
                                        + Adicionar Meta
                                      </Button>
                                    </div>
                                    
                                    <div className="overflow-x-auto">
                                      <table className="w-full text-sm">
                                        <thead className="bg-green-600 text-white">
                                          <tr>
                                            <th className="p-2 text-left">#</th>
                                            <th className="p-2 text-left">Meta (Luck Diamond)</th>
                                            <th className="p-2 text-center">Tempo Mínimo (horas)</th>
                                            <th className="p-2 text-center">Pontos</th>
                                            <th className="p-2 text-center">Ações</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {editedPointsTable
                                            .sort((a, b) => a.goal - b.goal)
                                            .map((row, index) => (
                                            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-green-50'}>
                                              <td className="p-2 font-bold text-gray-500">{index + 1}</td>
                                              <td className="p-2">
                                                <input
                                                  type="number"
                                                  min="0"
                                                  value={row.goal}
                                                  onChange={(e) => handleUpdateGoal(index, 'goal', parseInt(e.target.value) || 0)}
                                                  className="w-full px-2 py-1 border rounded text-right"
                                                />
                                              </td>
                                              <td className="p-2">
                                                <input
                                                  type="number"
                                                  step="0.5"
                                                  min="0.5"
                                                  value={row.min_hours}
                                                  onChange={(e) => handleUpdateGoal(index, 'min_hours', parseFloat(e.target.value) || 0.5)}
                                                  className="w-24 mx-auto px-2 py-1 border rounded text-center block"
                                                />
                                              </td>
                                              <td className="p-2">
                                                <input
                                                  type="number"
                                                  step="0.1"
                                                  min="0"
                                                  value={row.points}
                                                  onChange={(e) => handleUpdateGoal(index, 'points', parseFloat(e.target.value) || 0)}
                                                  className="w-24 mx-auto px-2 py-1 border rounded text-center font-bold text-purple-700 block"
                                                />
                                              </td>
                                              <td className="p-2 text-center">
                                                <Button
                                                  onClick={() => handleRemoveGoal(index)}
                                                  size="sm"
                                                  variant="ghost"
                                                  className="text-red-600 hover:text-red-800 hover:bg-red-100"
                                                >
                                                  🗑️
                                                </Button>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                    
                                    <p className="text-xs text-gray-500 mt-3">
                                      A meta é o valor mínimo de Luck Diamond que o host precisa atingir no dia.
                                      Se atingir a meta E o tempo mínimo, ganha os pontos correspondentes.
                                    </p>
                                  </div>

                                  {/* Preview do Cálculo */}
                                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                    <h3 className="font-bold text-gray-800 mb-2">📊 Exemplo de Cálculo</h3>
                                    <div className="text-sm text-gray-700 space-y-2">
                                      <p>Host com 10 pontos acumulados na semana:</p>
                                      <div className="space-y-1">
                                        {Object.entries(editedPresenceMultiplier)
                                          .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
                                          .map(([days, multiplier]) => (
                                            <p key={days} className="font-mono bg-white p-2 rounded text-xs">
                                              {days} dias: 10 pts × {multiplier} × {editedCrystalsMultiplier} = <strong className="text-green-700">{Math.floor(10 * multiplier * editedCrystalsMultiplier).toLocaleString('pt-BR')}</strong> cristais
                                            </p>
                                          ))
                                        }
                                      </div>
                                    </div>
                                  </div>
                                </>
                              )}
                            </CardContent>
                          </Card>
                        </TabsContent>
                        
                      </Tabs>

                    </>
                  )}
                </TabsContent>

                
                {/* Aba: Ranking de Estrelas */}
                <TabsContent value="ranking-estrelas" className="mt-4">
                  {!starRankingConfig?.active ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>Ranking desativado</p>
                      <label className="flex items-center gap-2 justify-center mt-4">
                        <input
                          type="checkbox"
                          checked={starRankingConfig?.active || false}
                          onChange={(e) => handleToggleStarRanking(e.target.checked)}
                          className="w-4 h-4"
                        />
                        <span className="font-medium">Ativar Ranking de Estrelas</span>
                      </label>
                    </div>
                  ) : (
                    <>
                      {/* Sub-abas do Ranking de Estrelas */}
                      <Tabs defaultValue="ranking-atual" className="w-full">
                        <TabsList className="grid w-full grid-cols-3 mb-4">
                          <TabsTrigger value="ranking-atual">⭐ Ranking Atual</TabsTrigger>
                          <TabsTrigger value="historico-estrelas" onClick={() => loadStarRankingHistory()}>📜 Histórico</TabsTrigger>
                          <TabsTrigger value="perdidas-estrelas" onClick={() => loadStarExpired()}>❌ Perdidas</TabsTrigger>
                        </TabsList>
                        
                        {/* Sub-aba: Ranking Atual */}
                        <TabsContent value="ranking-atual">
                          <Card>
                            <CardHeader>
                              <div className="flex justify-between items-center">
                                <CardTitle className="text-base sm:text-lg">⭐ Ranking da Semana Atual</CardTitle>
                                <label className="flex items-center gap-2 text-sm">
                                  <input
                                    type="checkbox"
                                    checked={starRankingConfig.active}
                                    onChange={(e) => handleToggleStarRanking(e.target.checked)}
                                    className="w-4 h-4"
                                  />
                                  <span className="font-medium">Ativo</span>
                                </label>
                              </div>
                              <p className="text-sm text-gray-600 mt-2">
                                Bônus: 🥇 {starRankingConfig.top1_percentage}% | 🥈 {starRankingConfig.top2_percentage}% | 🥉 {starRankingConfig.top3_percentage}%
                              </p>
                              
                              {/* Editar Percentuais */}
                              {!editingPercentages ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setTempPercentages({
                                      top1: starRankingConfig.top1_percentage,
                                      top2: starRankingConfig.top2_percentage,
                                      top3: starRankingConfig.top3_percentage
                                    });
                                    setEditingPercentages(true);
                                  }}
                                  className="mt-2"
                                >
                                  ✏️ Editar Percentuais
                                </Button>
                              ) : (
                          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded space-y-2">
                            <div className="grid grid-cols-3 gap-2">
                              <div>
                                <label className="text-xs font-semibold text-gray-700">🥇 1º Lugar (%)</label>
                                <Input
                                  type="number"
                                  step="0.1"
                                  min="0"
                                  max="100"
                                  value={tempPercentages.top1}
                                  onChange={(e) => setTempPercentages({...tempPercentages, top1: parseFloat(e.target.value) || 0})}
                                  className="text-sm"
                                />
                              </div>
                              <div>
                                <label className="text-xs font-semibold text-gray-700">🥈 2º Lugar (%)</label>
                                <Input
                                  type="number"
                                  step="0.1"
                                  min="0"
                                  max="100"
                                  value={tempPercentages.top2}
                                  onChange={(e) => setTempPercentages({...tempPercentages, top2: parseFloat(e.target.value) || 0})}
                                  className="text-sm"
                                />
                              </div>
                              <div>
                                <label className="text-xs font-semibold text-gray-700">🥉 3º Lugar (%)</label>
                                <Input
                                  type="number"
                                  step="0.1"
                                  min="0"
                                  max="100"
                                  value={tempPercentages.top3}
                                  onChange={(e) => setTempPercentages({...tempPercentages, top3: parseFloat(e.target.value) || 0})}
                                  className="text-sm"
                                />
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={handleSavePercentages}
                                className="flex-1"
                              >
                                💾 Salvar
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditingPercentages(false)}
                                className="flex-1"
                              >
                                ✕ Cancelar
                              </Button>
                            </div>
                          </div>
                        )}
                      </CardHeader>
                      <CardContent>
                        {loadingStarRanking ? (
                          <div className="flex justify-center p-8">
                            <FoxSpinnerInline size="md" />
                          </div>
                        ) : starRanking.length === 0 ? (
                          <p className="text-center text-gray-500 py-8">Clique na aba para carregar ranking</p>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-yellow-100 border-b-2">
                                <tr>
                                  <th className="text-center p-2 sm:p-3 font-semibold">Posição</th>
                                  <th className="text-left p-2 sm:p-3 font-semibold">Host</th>
                                  <th className="text-right p-2 sm:p-3 font-semibold">Renda do Host</th>
                                  <th className="text-center p-2 sm:p-3 font-semibold">Bônus</th>
                                </tr>
                              </thead>
                              <tbody>
                                {starRanking.map((host, idx) => (
                                  <tr key={host.show_id} className={`border-b ${idx < 3 ? 'bg-yellow-50' : 'hover:bg-gray-50'}`}>
                                    <td className="p-2 sm:p-3 text-center">
                                      <span className={`font-bold text-lg ${
                                        idx === 0 ? 'text-yellow-600' : 
                                        idx === 1 ? 'text-gray-500' : 
                                        idx === 2 ? 'text-orange-600' : ''
                                      }`}>
                                        {idx === 0 && '🥇'}
                                        {idx === 1 && '🥈'}
                                        {idx === 2 && '🥉'}
                                        {idx > 2 && `${idx + 1}º`}
                                      </span>
                                    </td>
                                    <td className="p-2 sm:p-3">
                                      <div className="flex items-center gap-2">
                                        <img
                                          src={host.avatar}
                                          alt={host.nickname}
                                          className="w-8 h-8 sm:w-10 sm:h-10 rounded-full"
                                        />
                                        <div>
                                          <p className="font-semibold text-xs sm:text-sm">{host.nickname}</p>
                                          <p className="text-[10px] sm:text-xs text-gray-500">{host.show_id}</p>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="p-2 sm:p-3 text-right">
                                      <p className="font-mono font-bold text-purple-700">
                                        {host.anchor_income.toLocaleString('pt-BR')} 💎
                                      </p>
                                    </td>
                                    <td className="p-2 sm:p-3 text-center">
                                      {host.is_top3 ? (
                                        <Badge className="bg-yellow-600">
                                          {idx === 0 ? `${starRankingConfig.top1_percentage}%` : 
                                           idx === 1 ? `${starRankingConfig.top2_percentage}%` : 
                                           `${starRankingConfig.top3_percentage}%`}
                                        </Badge>
                                      ) : (
                                        <span className="text-gray-400">-</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            
                            <div className="mt-4 text-center text-sm text-gray-600">
                              Total: {starRanking.length} hosts no ranking
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>
                  
                  {/* Sub-aba: Histórico de Resgates do Ranking de Estrelas */}
                  <TabsContent value="historico-estrelas" className="mt-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base sm:text-lg">📜 Histórico de Resgates - Ranking de Estrelas</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Filtros e Ordenação — modelo Tarefas VIP */}
                        <div className="grid gap-4 md:grid-cols-5">
                          <div className="space-y-2">
                            <Label className="text-sm">Show ID (Host)</Label>
                            <Input
                              placeholder="Filtrar por host"
                              value={starHistoryFilter}
                              onChange={(e) => setStarHistoryFilter(e.target.value)}
                              data-testid="star-history-filter-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm">Data Início</Label>
                            <Input
                              type="date"
                              value={starRankingDateFilter.start_date}
                              onChange={(e) => setStarRankingDateFilter({ ...starRankingDateFilter, start_date: e.target.value })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm">Data Fim</Label>
                            <Input
                              type="date"
                              value={starRankingDateFilter.end_date}
                              onChange={(e) => setStarRankingDateFilter({ ...starRankingDateFilter, end_date: e.target.value })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm">Ordenar por</Label>
                            <select
                              value={starRankingSortBy}
                              onChange={(e) => setStarRankingSortBy(e.target.value)}
                              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                            >
                              <option value="week_desc">Semana (Recente)</option>
                              <option value="week_asc">Semana (Antiga)</option>
                              <option value="coins_desc">Moedas (Maior)</option>
                              <option value="coins_asc">Moedas (Menor)</option>
                              <option value="claims_desc">Resgates (Maior)</option>
                              <option value="claims_asc">Resgates (Menor)</option>
                            </select>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm">&nbsp;</Label>
                            <Button 
                              onClick={() => loadStarRankingHistory(starHistoryFilter)}
                              disabled={loadingStarHistory}
                              className="w-full bg-green-600 hover:bg-green-700"
                              data-testid="star-history-filter-btn"
                            >
                              {loadingStarHistory ? 'Buscando...' : 'Aplicar Filtros'}
                            </Button>
                          </div>
                        </div>
                        
                        {loadingStarHistory ? (
                          <div className="flex justify-center p-8">
                            <FoxSpinnerInline size="md" />
                          </div>
                        ) : starRankingHistory.length === 0 ? (
                          <p className="text-center text-gray-500 py-8">Nenhum resgate realizado ainda</p>
                        ) : (
                          <div className="space-y-3">
                            {(() => {
                              // Filtros e ordenação client-side
                              let filtered = [...starRankingHistory];
                              if (starRankingDateFilter.start_date) {
                                filtered = filtered.filter(w => w.week_start >= starRankingDateFilter.start_date);
                              }
                              if (starRankingDateFilter.end_date) {
                                filtered = filtered.filter(w => w.week_end <= starRankingDateFilter.end_date);
                              }
                              filtered.sort((a, b) => {
                                switch (starRankingSortBy) {
                                  case 'week_desc':   return (b.week_start || '').localeCompare(a.week_start || '');
                                  case 'week_asc':    return (a.week_start || '').localeCompare(b.week_start || '');
                                  case 'coins_desc':  return (b.total_coins || 0) - (a.total_coins || 0);
                                  case 'coins_asc':   return (a.total_coins || 0) - (b.total_coins || 0);
                                  case 'claims_desc': return (b.claims?.length || 0) - (a.claims?.length || 0);
                                  case 'claims_asc':  return (a.claims?.length || 0) - (b.claims?.length || 0);
                                  default: return 0;
                                }
                              });
                              
                              if (filtered.length === 0) {
                                return <p className="text-center text-gray-500 py-8">Nenhum resgate no período filtrado</p>;
                              }
                              
                              return filtered.map((weekGroup) => {
                                const weekIds = weekGroup.claims.map((c) => c.id);
                                const weekAllSelected = weekIds.length > 0 && weekIds.every((id) => selectedStarClaims.includes(id));
                                const weekSomeSelected = weekIds.some((id) => selectedStarClaims.includes(id));
                                return (
                                <div key={weekGroup.week_start} className="border rounded-lg p-3 bg-green-50 mb-3">
                                  <div className="flex items-center justify-between mb-3">
                                    <h4 className="font-bold text-sm text-green-700">
                                      📅 Semana: {weekGroup.week_start} a {weekGroup.week_end}
                                    </h4>
                                    <Badge className="bg-green-600 text-white">
                                      {weekGroup.claims.length} resgate(s) • Total: {weekGroup.total_coins.toLocaleString('pt-BR')} 💎
                                    </Badge>
                                  </div>
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                      <thead className="bg-gray-100">
                                        <tr>
                                          <th className="p-2 w-8">
                                            <input
                                              type="checkbox"
                                              checked={weekAllSelected}
                                              ref={(el) => { if (el) el.indeterminate = !weekAllSelected && weekSomeSelected; }}
                                              onChange={(e) => toggleAllStarClaims(weekGroup.claims, e.target.checked)}
                                              data-testid={`star-week-select-all-${weekGroup.week_start}`}
                                            />
                                          </th>
                                          <th className="text-left p-2">Host</th>
                                          <th className="text-center p-2">Posição</th>
                                          <th className="text-right p-2">Renda</th>
                                          <th className="text-right p-2">Moedas</th>
                                          <th className="text-center p-2">Resgatado em</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {weekGroup.claims.map((claim) => (
                                          <tr key={claim.id} className="border-b hover:bg-white">
                                            <td className="p-2">
                                              <input
                                                type="checkbox"
                                                checked={selectedStarClaims.includes(claim.id)}
                                                onChange={() => toggleStarClaim(claim.id)}
                                                data-testid={`star-claim-checkbox-${claim.id}`}
                                              />
                                            </td>
                                            <td className="p-2">
                                              <div className="flex items-center gap-2">
                                                {claim.avatar && (
                                                  <img src={claim.avatar} alt={claim.nickname} className="w-7 h-7 rounded-full border" />
                                                )}
                                                <div className="text-xs">
                                                  <div className="font-medium">{claim.nickname || 'Desconhecido'}</div>
                                                  <div className="text-gray-500">{claim.show_id}</div>
                                                </div>
                                              </div>
                                            </td>
                                            <td className="p-2 text-center">
                                              <Badge className={
                                                claim.position === 1 ? 'bg-yellow-500 text-white text-xs' :
                                                claim.position === 2 ? 'bg-gray-400 text-white text-xs' :
                                                'bg-orange-400 text-white text-xs'
                                              }>
                                                {claim.position === 1 ? '🥇' : claim.position === 2 ? '🥈' : '🥉'} TOP {claim.position}
                                              </Badge>
                                            </td>
                                            <td className="p-2 text-right">
                                              <span className="font-mono text-xs text-purple-700">{(claim.anchor_income || 0).toLocaleString('pt-BR')}</span>
                                            </td>
                                            <td className="p-2 text-right">
                                              <Badge className="bg-green-600 text-white text-xs">
                                                +{(claim.bonus_coins || 0).toLocaleString('pt-BR')} 💎
                                              </Badge>
                                            </td>
                                            <td className="p-2 text-center text-xs">
                                              {claim.claimed_at ? new Date(claim.claimed_at).toLocaleString('pt-BR') : '-'}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                                );
                              });
                            })()}
                            
                            {/* Barra de ação para deletar selecionados */}
                            {selectedStarClaims.length > 0 && (
                              <div className="sticky bottom-2 z-10 mt-3 p-3 bg-red-50 border border-red-300 rounded-lg flex items-center justify-between gap-2">
                                <span className="text-sm text-red-700 font-semibold">
                                  {selectedStarClaims.length} resgate(s) selecionado(s)
                                </span>
                                <div className="flex gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setSelectedStarClaims([])}
                                  >
                                    Cancelar
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="bg-red-600 hover:bg-red-700 text-white"
                                    disabled={deletingClaims}
                                    onClick={deleteSelectedStarClaims}
                                    data-testid="star-delete-selected-btn"
                                  >
                                    {deletingClaims ? 'Apagando...' : '🗑️ Apagar selecionados'}
                                  </Button>
                                </div>
                              </div>
                            )}
                            
                            {/* Total Geral */}
                            <div className="mt-4 p-3 bg-green-50 rounded-lg">
                              <p className="text-center text-sm font-semibold text-green-700">
                                Total Geral: {starRankingHistory.reduce((sum, w) => sum + w.total_coins, 0).toLocaleString('pt-BR')} moedas em {starRankingHistory.reduce((sum, w) => sum + w.claims.length, 0)} resgates
                              </p>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>
                  
                  {/* Sub-aba: Recompensas Perdidas - Ranking de Estrelas */}
                  <TabsContent value="perdidas-estrelas" className="mt-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base sm:text-lg text-red-600">❌ Recompensas Perdidas - Ranking de Estrelas</CardTitle>
                        <p className="text-sm text-gray-500">Recompensas TOP 3 não resgatadas que expiraram</p>
                      </CardHeader>
                      <CardContent>
                        {/* Filtro por ID */}
                        <div className="flex gap-2 mb-4">
                          <Input
                            placeholder="Filtrar por ID do host..."
                            value={starExpiredFilter}
                            onChange={(e) => setStarExpiredFilter(e.target.value)}
                            className="max-w-xs"
                            data-testid="star-expired-filter-input"
                          />
                          <Button 
                            onClick={() => loadStarExpired(starExpiredFilter)}
                            disabled={loadingStarExpired}
                            data-testid="star-expired-filter-btn"
                          >
                            {loadingStarExpired ? 'Buscando...' : 'Buscar'}
                          </Button>
                          {starExpiredFilter && (
                            <Button 
                              variant="outline"
                              onClick={() => {
                                setStarExpiredFilter('');
                                loadStarExpired('');
                              }}
                            >
                              Limpar
                            </Button>
                          )}
                        </div>
                        
                        {loadingStarExpired ? (
                          <div className="flex justify-center p-8">
                            <FoxSpinnerInline size="md" />
                          </div>
                        ) : starExpired.length === 0 ? (
                          <p className="text-center text-gray-500 py-8">Nenhuma recompensa perdida encontrada</p>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-red-100 border-b-2">
                                <tr>
                                  <th className="text-left p-2 sm:p-3 font-semibold">Host</th>
                                  <th className="text-center p-2 sm:p-3 font-semibold">Semana</th>
                                  <th className="text-center p-2 sm:p-3 font-semibold">Posição</th>
                                  <th className="text-right p-2 sm:p-3 font-semibold">Renda</th>
                                  <th className="text-right p-2 sm:p-3 font-semibold">Moedas Perdidas</th>
                                  <th className="text-center p-2 sm:p-3 font-semibold">Expirou em</th>
                                </tr>
                              </thead>
                              <tbody>
                                {starExpired.map((reward) => (
                                  <tr key={reward.id} className="border-b hover:bg-red-50">
                                    <td className="p-2 sm:p-3">
                                      <div className="flex items-center gap-2">
                                        {reward.avatar && (
                                          <img
                                            src={reward.avatar}
                                            alt={reward.nickname}
                                            className="w-8 h-8 sm:w-10 sm:h-10 rounded-full"
                                          />
                                        )}
                                        <div>
                                          <p className="font-semibold text-xs sm:text-sm">{reward.nickname || 'Desconhecido'}</p>
                                          <p className="text-[10px] sm:text-xs text-gray-500">{reward.show_id}</p>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="p-2 sm:p-3 text-center">
                                      <p className="text-xs">{reward.week_start}</p>
                                      <p className="text-xs text-gray-500">a {reward.week_end}</p>
                                    </td>
                                    <td className="p-2 sm:p-3 text-center">
                                      <Badge className={
                                        reward.position === 1 ? 'bg-yellow-500' :
                                        reward.position === 2 ? 'bg-gray-400' :
                                        'bg-orange-400'
                                      }>
                                        {reward.position === 1 ? '🥇' : reward.position === 2 ? '🥈' : '🥉'} TOP {reward.position}
                                      </Badge>
                                    </td>
                                    <td className="p-2 sm:p-3 text-right">
                                      <p className="font-mono text-xs">{(reward.anchor_income || 0).toLocaleString('pt-BR')}</p>
                                    </td>
                                    <td className="p-2 sm:p-3 text-right">
                                      <p className="font-mono font-bold text-red-600">
                                        {(reward.bonus_coins || 0).toLocaleString('pt-BR')} 💎
                                      </p>
                                    </td>
                                    <td className="p-2 sm:p-3 text-center">
                                      <p className="text-xs text-red-600">
                                        {reward.expires_at ? new Date(reward.expires_at).toLocaleDateString('pt-BR') : '-'}
                                      </p>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            
                            <div className="mt-4 p-3 bg-red-50 rounded-lg">
                              <p className="text-center text-sm font-semibold text-red-700">
                                Total perdido: {starExpired.reduce((sum, r) => sum + (r.bonus_coins || 0), 0).toLocaleString('pt-BR')} moedas em {starExpired.length} recompensas
                              </p>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>
                  
                </Tabs>
                    </>
                  )}
                </TabsContent>

              </Tabs>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Nova Aba: Gerenciar Exclusões */}
        <TabsContent value="exclusions" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base sm:text-lg">⛔ Gerenciar Exclusões de Hosts</CardTitle>
              <p className="text-xs sm:text-sm text-gray-600 mt-2">
                Controle quais hosts podem participar de cada evento
              </p>
            </CardHeader>
            <CardContent>
              <EventExclusionsManager />
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>
    </div>
  );
};

export default AgencyManagement;
